/** Crisp plan geometry and interaction overlays, composed over an on-demand
 * orthographic render of the actual furniture meshes from the shared renderer. */
import { isAttic } from '../../state/attic';
import type { Item, Level, Opening, Room, Surface, Vec2, Wall } from '../../core/hp/types';
import { openingDesign, openingKind } from '../../state/openingProperties';
import { drawOpeningSymbol, openingSymbol, symbolHit, type OpeningSymbol, type SymbolFrame, type SymbolState } from './openingSymbols';
import { openingTwins } from '../../core/geometry/openingTwins';
import { buildWallSolids, segmentOutside, visibleOutlineEdges, type WallSolid } from '../../core/geometry/walls';
import type { Selection, EditorState } from '../../state/store';
import { floorPatternAffine } from '../../core/hp/surfaceTransform';
import { contains, polygonArea, projectPoint, roomProbe, samePoint, DEFAULT_WALL_THICKNESS, MIN_WALL } from '../../core/geometry/edit';
import { standardAngle } from '../../state/planGesture';
import { itemFootprint, localToPlan, type Footprints, type LocalFootprint } from '../../core/geometry/footprint';
import { floorArea, roomsBesideWall } from '../../core/geometry/roomFloor';
import { isObstruction } from '../../core/geometry/overlap';
import { distanceLabel, distanceMarkers, openingDistanceMarkers, type DistanceMarker, type SnapGuide } from '../../core/geometry/snapping';
import { isPlanItemVisible } from '../../state/planVisibility';
import type { PlanFurnitureLayer } from '../three/PlanTopView';
import type { SurfaceProbe } from '../../core/geometry/stacking';
import { resolveTexture, roomSurfaceOpacity } from '../textures';
import { subscribeBuiltinTextures } from '../../state/builtinTextures';
import { subscribeModelTags } from '../../state/modelTags';
import { fallbackResizeBounds, separateHandles, type ResizeBounds, type ResizeFace } from '../../state/itemResize';
import { PLAN_SCALE_LIMITS } from '../../state/projectView';
import { roomTypeTitle } from '../../state/roomTypes';
import type { Stairwell } from '../../core/geometry/stairwells';
import { areaWithHeadroom, ceilingEdges, FIVE_FEET, headroomLine, roomCeiling, SEVEN_FEET, slopedCeilings } from '../../state/roomCeiling';
import { fixed, t as tr } from '../../i18n/I18n';

export { localToPlan } from '../../core/geometry/footprint';

export interface PlanDraft { start: Vec2; end: Vec2; valid: boolean; kind?: 'room' | 'opening'; opening?: Opening; wall?: Wall;
  /** An opening draft computed with snapping bypassed (Ctrl/⌘), so a modifier change is a change. */
  snapDisabled?: boolean;
  /** A wall draft whose moving end holds existing geometry: the anchor is drawn as the square Wall Snap Anchor. */
  snapped?: boolean;
  /** Shorter than a wall can be — not yet a wall rather than a refused one, so nothing turns red. */
  pending?: boolean;
  /** The thickness the wall will be built with; the body is previewed at that size. */
  thickness?: number }
/** The wall-tool overlays around the draft: everything the Figma wall rules draw besides the wall itself. */
/**
 * A dimension label as last drawn, in canvas pixels: a wall's length or a room's area, which a tap types over
 * (`labelAt`). `x`, `y` are its middle, `angle` its turn, the halves its size.
 */
export type DimensionLabel = ({ kind: 'wall'; id: string } | { kind: 'room'; index: number })
  & { x: number; y: number; angle: number; halfWidth: number; halfHeight: number; value: number };
export const dimensionLabelKey = (label: Pick<DimensionLabel, 'kind'> & ({ id: string } | { index: number })): string =>
  'id' in label ? `wall:${label.id}` : `room:${label.index}`;

export interface PlanOverlay {
  /** The dimension labels take a real value (the plan's scale is being set): they are drawn as buttons. */
  scaleMode?: boolean;
  /** The label under the pointer or being typed over, lit (`dimensionLabelKey`). */
  labelFocus?: string | null;
  /** The Wall Anchor under the pointer before a wall is begun. */
  anchor?: { point: Vec2; snapped: boolean } | null;
  /** Where the next wall of a chain starts. */
  chain?: Vec2 | null;
  /** Geometry the plan refused, shown red where the pointer asked for it. */
  invalid?: { quads: Vec2[][]; handle?: Vec2 } | null;
  /** An active capture: the wall gets a turquoise edge, the moved endpoint a turquoise handle. */
  snap?: { wallId?: string; handle?: Vec2 } | null;
  /** The ruler's lines (the app's measuring mode), the one being pulled last: each with its ends and its length. */
  measures?: ReadonlyArray<{ from: Vec2; to: Vec2 }>;
  /** A wall gesture is in progress: alignment guides speak the snap language rather than the furniture one. */
  accent?: boolean;
  /** A drag is in flight: resting affordances such as the corner-to-be stay out of the way. */
  dragging?: boolean;
}
export type PlanGuide = SnapGuide;

export interface Viewport {
  /** Plan metres per CSS pixel. */
  scale: number;
  /** Plan-space point at the canvas centre. */
  centre: Vec2;
}

export type HitTarget =
  | { kind: 'item'; id: string }
  | { kind: 'opening'; id: string }
  | { kind: 'wall'; id: string; side: 'front' | 'back' }
  | { kind: 'vertex'; wallId: string; end: 'start' | 'end' }
  /** The corner-to-be at the middle of a wall: pulling it puts a real corner there. */
  | { kind: 'wall-mid'; id: string }
  | { kind: 'room'; index: number }
  | { kind: 'room-move'; index: number }
  | { kind: 'rotate'; id: string }
  | ({ kind: 'resize'; id: string } & ResizeFace)
  | { kind: 'opening-resize'; id: string; side: -1 | 1 }
  | null;

/** Radius of the round rotation handle, in CSS pixels. */
const HANDLE_RADIUS = 9;
/** Resize handle centres at least this far apart: a 20 px disc, 22 when hot, and a gap. */
const PLAN_HANDLE_CLEAR = 26;

/**
 * The rotation glyph inside the handle: one open ring with a solid head, the same clockwise
 * arrow the toolbar icon draws. Painted with plain arcs so it stays a single canvas pass.
 */
function drawRotateGlyph(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  const radius = 4.3, start = -Math.PI * .1, end = Math.PI * 1.6;
  ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.arc(x, y, radius, start, end); ctx.stroke();
  ctx.lineCap = 'butt';
  const tip = { x: x + Math.cos(end) * radius, y: y + Math.sin(end) * radius };
  const along = { x: -Math.sin(end), y: Math.cos(end) }, out = { x: Math.cos(end), y: Math.sin(end) };
  ctx.beginPath();
  ctx.moveTo(tip.x + along.x * 2.4, tip.y + along.y * 2.4);
  ctx.lineTo(tip.x - along.x * 1.1 + out.x * 2.1, tip.y - along.y * 1.1 + out.y * 2.1);
  ctx.lineTo(tip.x - along.x * 1.1 - out.x * 2.1, tip.y - along.y * 1.1 - out.y * 2.1);
  ctx.closePath(); ctx.fillStyle = color; ctx.fill();
}

/** CSS pixels between a wall's room-side face and the distance markers of an opening moved along it: clear of the outline stroke. */
const OPENING_MARKER_LIFT = 14;

const COLOR = {
  /** The Figma «Grid canvas»: near-white paper, a thin line per step and a stronger one every fifth. */
  paper: '#fbfbfc',
  grid: '#e9eaee',
  gridMajor: '#d6d7de',
  /** Wall states from the Figma «Walls & States» sheet: ready, hover, selected + hover. */
  wall: '#464955',
  wallEdge: '#30323b',
  wallHover: '#6b6d77',
  wallSelectedHover: '#6ea6ff',
  /** A wall of the floor below: the ready colour at 30 % over the paper, opaque so that overlapping bodies stay one tint. */
  referenceWall: '#c5c6ca',
  /** A hidden wall: only its dashed outline, over a faint body while it is hovered or selected. */
  hiddenWallHover: 'rgba(70,73,85,.14)',
  hiddenWallSelected: 'rgba(20,131,224,.16)',
  handleEdge: '#8b8d96',
  /** The snap accent: anchors, handles and the edge of a wall that hold existing geometry. */
  snap: '#3ae0b6',
  snapMark: '#1ddead',
  /** The Wall Axis: turquoise on a standard angle, grey while the direction is free. */
  axisSnap: '#73e1c5',
  axisFree: '#bebfc7',
  /** A wall still being drawn, and geometry the plan refuses. */
  draftWall: '#a2cbed',
  invalid: '#f94c46',
  invalidFill: '#f2a9a4',
  /** An opening's Points (the Figma rules, card 11): the ring in Hover and in Selected, and the point under the pointer or dragged. */
  pointHover: '#2e3140',
  pointSelected: '#1483e0',
  pointHot: 'rgba(8,74,255,0.75)',
  room: '#ffffff',
  roomEdge: '#c9c5bc',
  roomLabel: '#8a857c',
  /** Dimension labels while the plan's scale is set: the chrome's blue. */
  labelScale: '#1483e0',
  /** A stairwell: the outline and the diagonals of an opening in a slab. */
  well: '#8a857c',
  /** Under a mansard: the lines where its ceiling's surfaces meet, and the 5 ft and 7 ft headroom lines (ANSI Z765). */
  ceilingEdge: '#5f5b53',
  headroomLow: '#a8641c',
  headroomTall: '#3f6c99',
  item: '#cdbfa4',
  itemEdge: '#8b7f68',
  itemWall: '#bda8cf',
  itemCeiling: '#a2c9bb',
  select: '#4085ff',
  /** Unity outlines whatever the placed item is driven into in red; so does the plan. */
  conflict: '#ff3b30',
  dim: '#9a958c',
  text: '#2c2b28',
};

export class PlanView {
  /** What the last frame measured for an opening being moved — read by tests and the canvas dataset. */
  drawnOpeningMarkers: DistanceMarker[] = [];
  /** The symbol and state each opening was drawn with in the last frame, the draft's included — read by tests and browser checks. */
  drawnOpeningSymbols: Array<{ id: string; state: SymbolState } & OpeningSymbol> = [];
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private width = 1;
  private height = 1;
  private solids: WallSolid[] = [];
  /** The wall bodies of the floor below, per level drawn: that floor does not change while this one is edited. */
  private referenceSolids = new WeakMap<Level, WallSolid[]>();
  /** How many walls of the floor below the last frame drew under this floor — read by tests and browser checks. */
  drawnReferenceWalls = 0;
  /** How many stairwells the last frame drew, for checks. */
  drawnWells = 0;
  /** How many lines of sloped ceilings the last frame drew: its folds, hips and steps, and its 5 ft and 7 ft lines. */
  drawnCeilingLines = 0;
  /** The outline pieces on the boundary of the walls' union, per wall — seams inside the solid are not drawn. */
  private seams = new WeakMap<WallSolid[], Map<string, Array<[Vec2, Vec2]>>>();
  /** Each wall's dimension chain, already clipped to the space outside every wall. */
  private chains = new WeakMap<WallSolid[], Map<string, Array<[Vec2, Vec2]>>>();

  private furnitureLayer: PlanFurnitureLayer | null = null;
  private footprints: Footprints = new Map();
  private footprintItems: Level['items'] | null = null;
  private readyFootprints = new Map<string, LocalFootprint>();
  private surfaceImages = new Map<string, { image: HTMLImageElement; loaded: boolean; pattern?: CanvasPattern }>();
  /**
   * While a floor is drawn as an image (`renderPlanImage`): white paper without a grid, the scale bar in the corner, and in
   * black and white for the app's two drawings for estate agents and designers.
   */
  print: PlanExportStyle | null = null;
  private surfaceRooms: Level['rooms'] | null = null;
  private labelRects: Array<{ x: number; y: number; width: number; height: number }> = [];
  /** The wall lengths and room areas the last frame drew, for a tap to type over (`labelAt`). */
  dimensionLabels: DimensionLabel[] = [];
  private labelStyle: { scale: boolean; focus: string | null } = { scale: false, focus: null };
  /** The sizes beside the resize handles as last drawn, in canvas pixels: the context bar keeps off them. */
  drawnResizeLabels: Array<{ x: number; y: number; width: number; height: number }> = [];
  /** The plan being drawn: floors or ceilings, which is also what a doorway shows. */
  private planSurface: 'floor' | 'ceiling' = 'floor';

  setFurnitureLayer(layer: PlanFurnitureLayer | null): void { this.furnitureLayer = layer; }
  /** The height of `item`'s loaded surface under `points`, from the plan's furniture meshes, or null without them. */
  readonly surfaceHeight: SurfaceProbe = Object.assign(
    (item: Item, points: readonly Vec2[]) => this.furnitureLayer?.surfaceHeight?.(item, points) ?? null,
    { layers: (item: Item, points: readonly Vec2[]) => this.furnitureLayer?.surfaceHeight?.layers?.(item, points) ?? null });

  invalidateFurniture(): void { this.footprintItems = null; }
  private measuredFootprints(level: Level): Map<string, LocalFootprint> {
    if (this.footprintItems === level.items) return this.readyFootprints;
    const result = new Map<string, LocalFootprint>();
    for (const item of level.items) {
      const bounds = this.furnitureLayer?.footprint(item);
      if (bounds) result.set(item.id, bounds);
    }
    this.footprintItems = level.items; this.readyFootprints = result;
    return result;
  }
  captureFootprints(level: Level): Map<string, LocalFootprint> { return new Map(this.measuredFootprints(level)); }

  viewport: Viewport = { scale: 0.012, centre: { x: 0, z: 0 } };

  /** A floor texture the catalog answered after the last draw: draw again with its picture. */
  private unsubscribeBuiltins = subscribeBuiltinTextures(() => this.invalidate());
  /** A door's or window's tags arrived from the catalog: its symbol may be another one. */
  private unsubscribeTags = subscribeModelTags(() => this.invalidate());

  constructor(private canvas: HTMLCanvasElement, private invalidate: () => void = () => {}) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
    this.resize();
  }

  dispose(): void {
    this.unsubscribeBuiltins();
    this.unsubscribeTags();
    for (const entry of this.surfaceImages.values()) entry.image.onload = entry.image.onerror = null;
    this.surfaceImages.clear();
  }

  /** False until the canvas has been laid out; fitting before then picks a wrong zoom. */
  hasSize(): boolean { return this.width > 40 && this.height > 40; }

  /** A canvas of its own size, not laid out on the page: an image of the plan. */
  setExportSize(width: number, height: number, dpr: number): void {
    this.dpr = dpr; this.width = width; this.height = height;
    this.canvas.width = Math.round(width * dpr); this.canvas.height = Math.round(height * dpr);
  }

  /** Whether the floors' pictures the last draw asked for are all here (or failed): an image waits for them. */
  surfacesSettled(): boolean {
    // A picture already in the browser's cache is complete before its load event says so.
    for (const entry of this.surfaceImages.values()) if (!entry.loaded && entry.image.complete && entry.image.naturalWidth > 0) entry.loaded = true;
    return [...this.surfaceImages.values()].every(entry => entry.loaded || entry.image.complete);
  }

  resize(): void {
    this.dpr = Math.min(devicePixelRatio || 1, 2);
    this.width = this.canvas.clientWidth || 1;
    this.height = this.canvas.clientHeight || 1;
    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
  }

  // ---- coordinate transforms ----------------------------------------------
  /** Plan metres → canvas CSS pixels. Same orientation as the 3D top view: +x right, +z down. */
  /** Centre the view on a point with `extent` metres filling ~70% of the shorter side. */
  focus(centre: Vec2, extent: number): void {
    this.viewport.centre = { x: centre.x, z: centre.z };
    this.viewport.scale = Math.max(extent / (Math.max(1, Math.min(this.width, this.height)) * 0.7), 1e-3);
  }

  toScreen(p: Vec2): { x: number; y: number } {
    return {
      x: this.width / 2 + (p.x - this.viewport.centre.x) / this.viewport.scale,
      y: this.height / 2 + (p.z - this.viewport.centre.z) / this.viewport.scale,
    };
  }

  toPlan(x: number, y: number): Vec2 {
    return {
      x: this.viewport.centre.x + (x - this.width / 2) * this.viewport.scale,
      z: this.viewport.centre.z + (y - this.height / 2) * this.viewport.scale,
    };
  }

  /**
   * Fit between the floating toolbars; padding is in CSS pixels at every DPR. A floor with nothing drawn on it yet is
   * framed on `reference`, the floor below that shows through it.
   */
  fit(level: Level, reference?: Level): void {
    let pts: Vec2[] = [
      ...level.walls.flatMap((w) => [w.start, w.end]),
      ...level.rooms.flatMap((r) => r.polygon),
      ...level.items.map((i) => i.position),
    ];
    if (!pts.length && reference) pts = [...reference.walls.flatMap((w) => [w.start, w.end]), ...reference.rooms.flatMap((r) => r.polygon)];
    if (!pts.length) return;
    const xs = pts.map((p) => p.x);
    const zs = pts.map((p) => p.z);
    const w = Math.max(Math.max(...xs) - Math.min(...xs), 1);
    const h = Math.max(Math.max(...zs) - Math.min(...zs), 1);
    // The chrome's claim, dimension rows included: the construction rail on the right (32 + 68 px), the
    // «ИИ-агент» launcher under the header on the left (down to 158 px), the 60 px bottom row.
    const left = Math.min(132, this.width * 0.2), right = Math.min(132, this.width * 0.2);
    const top = Math.min(174, this.height * 0.3), bottom = Math.min(108, this.height * 0.25);
    this.viewport.scale = clamp(Math.max(w / Math.max(1, this.width - left - right), h / Math.max(1, this.height - top - bottom)), PLAN_SCALE_LIMITS.min, PLAN_SCALE_LIMITS.max);
    this.viewport.centre = {
      x: (Math.max(...xs) + Math.min(...xs)) / 2 - (left - right) * this.viewport.scale / 2,
      z: (Math.max(...zs) + Math.min(...zs)) / 2 - (top - bottom) * this.viewport.scale / 2,
    };
  }

  zoomAt(screenX: number, screenY: number, factor: number): void {
    const before = this.toPlan(screenX, screenY);
    this.viewport.scale = clamp(this.viewport.scale * factor, PLAN_SCALE_LIMITS.min, PLAN_SCALE_LIMITS.max);
    const after = this.toPlan(screenX, screenY);
    this.viewport.centre.x += before.x - after.x;
    this.viewport.centre.z += before.z - after.z;
  }

  panBy(dxPx: number, dyPx: number): void {
    this.viewport.centre.x -= dxPx * this.viewport.scale;
    this.viewport.centre.z -= dyPx * this.viewport.scale;
  }

  // ---- drawing -------------------------------------------------------------
  /**
   * `reference` is the floor below, drawn faintly under this floor's own drawing while it is built (`floorBelow`):
   * its full-height walls in the wall colour at 30 %, as the app's room editing mode draws them.
   */
  draw(level: Level, selection: Selection, opts: { dimensions: boolean; surface: 'floor' | 'ceiling'; reference?: Level; wells?: readonly Stairwell[]; draft?: PlanDraft | null; guides?: PlanGuide[]; message?: string | null; hover?: HitTarget; footprints?: Footprints; dragging?: boolean; placing?: boolean; resize?: boolean; resizeFace?: ResizeFace; resizeHot?: ResizeFace['axis'] | null; resizeStart?: Item; resizeObstacles?: Array<{ x: number; y: number; width: number; height: number }>; openingResize?: { id: string; side: -1 | 1 }; openingHandles?: boolean; movingOpening?: boolean; materialPreview?: EditorState['materialTransformPreview'] } & PlanOverlay): void {
    const ctx = this.ctx;
    if (opts.draft?.kind === 'opening') selection = null;
    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.clearRect(0, 0, this.width, this.height);
    ctx.fillStyle = this.print ? '#ffffff' : COLOR.paper;
    ctx.fillRect(0, 0, this.width, this.height);
    if (!level) { ctx.restore(); return; }

    if (this.surfaceRooms !== level.rooms) {
      const urls = new Set(level.rooms.flatMap(room => [room.floorSurface, room.ceilingSurface]).map(surface => resolveTexture(surface)?.url));
      for (const [url, entry] of this.surfaceImages) if (!urls.has(url)) {
        entry.image.onload = entry.image.onerror = null; this.surfaceImages.delete(url);
      }
      this.surfaceRooms = level.rooms;
    }
    this.solids = buildWallSolids(level);
    if (!this.seams.has(this.solids)) this.seams.set(this.solids, visibleOutlineEdges(this.solids));

    this.labelRects = []; this.drawnResizeLabels = []; this.dimensionLabels = [];
    this.labelStyle = { scale: !!opts.scaleMode, focus: opts.labelFocus ?? null };
    this.planSurface = opts.surface;
    if (!this.print) this.drawGrid();
    this.drawnReferenceWalls = opts.reference ? this.drawReference(opts.reference) : 0;
    for (const room of level.rooms) {
      const p=opts.materialPreview;
      const preview=p&&(p.target.kind==='floor'||p.target.kind==='ceiling')&&p.target.roomIndex===room.index
        ?{...room,[p.target.kind==='floor'?'floorSurface':'ceilingSurface']:p.draft}:room;
      this.drawRoom(preview,selection,opts.surface,level);
    }
    this.drawnWells = this.drawWells(opts.wells ?? []);
    this.drawnCeilingLines = this.drawCeilingLines(level);
    const furniture = this.furnitureLayer?.render({ level, viewport: this.viewport, width: this.width, height: this.height, surface: opts.surface, dpr: this.dpr });
    const readyFootprints = this.measuredFootprints(level);
    this.footprints = opts.footprints ?? readyFootprints;
    if (furniture) {
      // The drawings in black and white take the furniture in grey, as the app's do.
      if (this.print === 'furniture' || this.print === 'architect') ctx.filter = 'grayscale(1) contrast(1.1)';
      ctx.drawImage(furniture, 0, 0, this.width, this.height);
      ctx.filter = 'none';
    }
    for (const item of level.items) {
      if (!isPlanItemVisible(item, opts.surface, level.height)) continue;
      if (!furniture || !readyFootprints.has(item.id)) this.drawItemPlaceholder(item);
    }
    for (const solid of this.solids) this.drawWall(solid, selection, opts.hover, opts.snap?.wallId === solid.wall.id);
    // Openings go over every wall, so no neighbouring wall body covers a doorway or a swing. One is in
    // Hover while the pointer is over it or one of its points, and while one of its points is dragged;
    // Selected wins over Hover. Of a door listed on both rooms' copies of a wall one is drawn, and it
    // shows its twin's selection and hover as its own.
    this.drawnOpeningSymbols = [];
    const twins = openingTwins(level), shownFor = (id?: string) => id && (twins.get(id) ?? id);
    const handles = opts.openingHandles !== false, resizing = handles ? opts.openingResize : undefined;
    const hovering = handles && !opts.dragging && (opts.hover?.kind === 'opening' || opts.hover?.kind === 'opening-resize') ? opts.hover : undefined;
    const hoveredOpening = shownFor(resizing?.id ?? hovering?.id), resizedOpening = shownFor(resizing?.id);
    const selectedShown = selection?.kind === 'opening' ? shownFor(selection.id) : undefined;
    for (const solid of this.solids) for (const opening of level.openings)
      if (opening.wallId === solid.wall.id && !twins.has(opening.id))
        this.drawOpening(opening, solid.wall, level, opening.id === selectedShown ? 'selected' : opening.id === hoveredOpening ? 'hover' : 'default');
    const selectedOpening = selection?.kind === 'opening' ? level.openings.find(opening => opening.id === selection.id) : undefined;
    const resizedWall = level.walls.find(wall => wall.id === level.openings.find(opening => opening.id === resizedOpening)?.wallId);
    if (opts.dimensions) for (const solid of this.solids) {
      if ((opts.draft?.kind === 'opening' && solid.wall.id === opts.draft.wall?.id) || solid.wall.id === resizedWall?.id) continue;
      // The architect's drawing measures a wall with doors or windows by its whole chain, as the app's design project
      // does (LengthMarkersController): outside an outside wall, and inside its own room a wall with rooms on both
      // sides, so the two rooms' copies of a wall in an app's project are each measured in their room, with their doors.
      const architect = this.print === 'architect';
      const openings = architect ? level.openings.filter(opening => opening.wallId === solid.wall.id) : [];
      const side = architect && roomsBesideWall(level, solid.wall).back ? -1 : 1;
      if (openings.length && !solid.wall.hidden) this.drawWallChain(solid.wall, openings, side); else this.drawWallDimension(solid, side);
    }
    this.drawSelectionOverlay(level, selection, opts.surface, opts.dimensions, (opts.dragging ?? false)||!!opts.resize, opts.placing ?? false);
    if (selection?.kind === 'room') {
      const room = level.rooms.find(r => r.index === selection.index);
      if (room) this.drawRoomMoveHandle(room, !!opts.dragging);
    }
    if(opts.resize&&selection?.kind==='item'){
      const item=level.items.find(it=>it.id===selection.id);
      if(item&&!item.locked&&isPlanItemVisible(item,opts.surface,level.height)){
        // The handle under the pointer is hot on its own; a size in the resize bar lights both faces of its axis.
        const hovered=opts.hover?.kind==='resize'?opts.hover:undefined,bounds=this.resizeBounds(item);
        const handles=this.resizeHandles(item).map(h=>{
          const active=opts.resizeFace?.axis===h.axis&&opts.resizeFace.side===h.side;
          return {...h,active,hot:active||(hovered?hovered.axis===h.axis&&hovered.side===h.side:opts.resizeHot===h.axis)};
        }).filter(h=>!opts.dragging||h.active);
        // The footprint the dragged face started from, dotted.
        if(opts.dragging&&opts.resizeStart){ctx.save();ctx.setLineDash([4,4]);ctx.beginPath();
          itemFootprint(opts.resizeStart,this.footprints).forEach((point,i)=>{const p=this.toScreen(point);if(i)ctx.lineTo(p.x,p.y);else ctx.moveTo(p.x,p.y);});
          ctx.closePath();ctx.strokeStyle='rgba(85,85,90,0.6)';ctx.lineWidth=1.2;ctx.stroke();ctx.restore();}
        for(const h of handles)if(h.hot)this.drawResizeEdge(item,bounds,h);
        for(const h of handles)if(h.anchor){ctx.beginPath();ctx.moveTo(h.anchor.x,h.anchor.y);ctx.lineTo(h.x,h.y);ctx.strokeStyle='rgba(20,131,224,0.8)';ctx.lineWidth=1.5;ctx.stroke();}
        for(const h of handles)this.drawResizeGrip(h.x,h.y,h.angle-Math.PI/2,h.active?'active':h.hot?'hot':'idle');
        // Each size beside its far handle, the width's on the right and the depth's below, as in 3D.
        const lit=handles.find(h=>h.active)?.axis??(hovered?.axis??opts.resizeHot??null);
        for(const axis of ['w','d'] as const){
          const pair=handles.filter(h=>h.axis===axis);if(!pair.length)continue;
          // The far handle first; then the near one, then either inward, whichever no panel covers.
          const [far,near]=[...pair].sort((a,b)=>axis==='w'?b.x-a.x:b.y-a.y);
          const delta=opts.dragging&&opts.resizeStart?Math.round(item.size[axis]*100)-Math.round(opts.resizeStart.size[axis]*100):0;
          const text=tr('#ValueCentimeters',Math.round(item.size[axis]*100)),extra=delta?` ${delta>0?'+':'−'}${Math.abs(delta)}`:'';
          const places=[far,near].filter(Boolean).flatMap(h=>[h,{...h,dir:{x:-h.dir.x,y:-h.dir.y}}]);
          const free=(rect:{x:number;y:number;width:number;height:number})=>rect.x>=0&&rect.y>=0&&rect.x+rect.width<=this.width&&rect.y+rect.height<=this.height
            &&!(opts.resizeObstacles??[]).some(o=>rect.x<o.x+o.width&&o.x<rect.x+rect.width&&rect.y<o.y+o.height&&o.y<rect.y+rect.height);
          const place=places.find(h=>free(this.resizeLabelRect(h,axis,text+extra)))??far;
          this.drawResizeLabel(place,axis,text,delta,lit===axis?'hot':lit?'dim':'idle');
        }
      }
    }
    this.drawGeometryOverlay(level, selection, opts.draft, opts.guides, opts.message, opts.hover, opts);
    for (const line of opts.measures ?? []) {
      const length = Math.hypot(line.to.x - line.from.x, line.to.z - line.from.z);
      if (length > 1e-6) this.drawDistance(line.from, line.to, length, '#20201f', 'primary');
      for (const end of [line.from, line.to]) this.drawAnchor(end, false);
    }
    // A door or window being moved along its wall measures its way to what it would run into, as a
    // moved piece of furniture does, and under the same conditions: dimensions on, a readable zoom.
    // Once the opening has snapped to an even spacing, the turquoise equal-gap guides already say
    // how far it is from its neighbours; the blue readout would only repeat them.
    this.drawnOpeningMarkers = [];
    if (opts.movingOpening && selectedOpening && opts.dimensions && this.viewport.scale <= 1 / 12 && !opts.guides?.length) {
      const lift = OPENING_MARKER_LIFT * this.viewport.scale;
      for (const marker of openingDistanceMarkers(level, selectedOpening.id)) {
        const shift = { x: marker.inward.x * lift, z: marker.inward.z * lift };
        const from = { x: marker.from.x + shift.x, z: marker.from.z + shift.z }, to = { x: marker.to.x + shift.x, z: marker.to.z + shift.z };
        this.drawDistance(from, to, marker.length, COLOR.select);
        this.drawnOpeningMarkers.push({ from, to, length: marker.length });
      }
    }
    // The Points of the opening in Hover and of the selected one; any other gesture hides them. While
    // one is dragged the opening's dimensions along its wall show, and its width beside that point.
    if (handles) for (const id of new Set([hoveredOpening, selectedShown])) {
      const opening = id ? level.openings.find(value => value.id === id) : undefined, wall = level.walls.find(value => value.id === opening?.wallId);
      if (!opening || !wall || (opts.dragging && id !== resizedOpening)) continue;
      const dragged = id === resizedOpening ? resizing!.side : undefined;
      const hot = dragged ?? (hovering?.kind === 'opening-resize' && shownFor(hovering.id) === id ? hovering.side : undefined);
      if (dragged) this.drawOpeningDimensions(opening, wall, false);
      this.drawOpeningResizeHandles(opening, wall, id === selectedShown ? 'selected' : 'hover', hot, !!dragged);
      if (dragged) this.drawOpeningWidthLabel(opening, wall, dragged);
    }
    this.drawScaleBar();
    ctx.restore();
  }

  /** The full-height walls of `level`, the floor below, as a flat 30 % tint of the wall colour; nothing of it is picked. */
  private drawReference(level: Level): number {
    let solids = this.referenceSolids.get(level);
    if (!solids) {
      // Only what holds up the floor above: the app leaves the low walls of the floor below out (WallController.IsFullHeight).
      solids = buildWallSolids(level).filter(solid => !solid.wall.hidden && solid.wall.height >= level.height - 0.005);
      this.referenceSolids.set(level, solids);
    }
    const ctx = this.ctx;
    ctx.fillStyle = COLOR.referenceWall;
    for (const solid of solids) {
      const c = solid.corners;
      ctx.beginPath();
      for (const [i, p] of (solid.outline ?? [c.leftStart, c.leftEnd, c.rightEnd, c.rightStart]).entries()) {
        const s = this.toScreen(p);
        if (i) ctx.lineTo(s.x, s.y); else ctx.moveTo(s.x, s.y);
      }
      ctx.closePath();
      ctx.fill();
    }
    return solids.length;
  }

  private drawGrid(): void {
    const ctx = this.ctx;
    const step = gridStep(this.viewport.scale);
    const tl = this.toPlan(0, 0);
    const br = this.toPlan(this.width, this.height);
    ctx.lineWidth = 1;
    for (let x = Math.ceil(tl.x / step) * step; x <= br.x; x += step) {
      const s = this.toScreen({ x, z: 0 }).x;
      ctx.strokeStyle = Math.abs(x % (step * 5)) < 1e-6 ? COLOR.gridMajor : COLOR.grid;
      ctx.beginPath(); ctx.moveTo(s, 0); ctx.lineTo(s, this.height); ctx.stroke();
    }
    for (let z = Math.ceil(tl.z / step) * step; z <= br.z; z += step) {
      const s = this.toScreen({ x: 0, z }).y;
      ctx.strokeStyle = Math.abs(z % (step * 5)) < 1e-6 ? COLOR.gridMajor : COLOR.grid;
      ctx.beginPath(); ctx.moveTo(0, s); ctx.lineTo(this.width, s); ctx.stroke();
    }
  }

  private drawRoom(room: Room, selection: Selection, planSurface: 'floor' | 'ceiling', level: Level): void {
    if (room.polygon.length < 3) return;
    const ctx = this.ctx;
    ctx.beginPath();
    room.polygon.forEach((p, i) => {
      const s = this.toScreen(p);
      if (i) ctx.lineTo(s.x, s.y); else ctx.moveTo(s.x, s.y);
    });
    ctx.closePath();
    const selected = selection?.kind === 'room' && selection.index === room.index;
    const surface = planSurface === 'ceiling' ? room.ceilingSurface : room.floorSurface;
    const mono = this.print === 'furniture' || this.print === 'architect';
    ctx.save();
    ctx.globalAlpha = mono ? 1 : roomSurfaceOpacity(surface) ?? 1;
    ctx.fillStyle = mono ? '#ffffff' : surface?.color ? `rgb(${surface.color.map(c => Math.round(clamp(c, 0, 1) * 255)).join(',')})`
      : planSurface === 'ceiling' ? '#f6f5f3' : '#d8d2c8';
    ctx.fill();
    const pattern = mono ? null : this.surfacePattern(surface);
    if (pattern) { ctx.fillStyle = pattern; ctx.fill(); }
    ctx.restore();
    // Selection remains a tint over the chosen finish, not a replacement material.
    if (selected) { ctx.fillStyle = '#4085ff26'; ctx.fill(); }
    ctx.strokeStyle = COLOR.roomEdge;
    ctx.lineWidth = 1;
    ctx.stroke();

    const c = selected ? roomMoveHandlePoint(room) : centroid(room.polygon);
    const s = this.toScreen(c);
    if (selected) s.y += 38;
    ctx.textAlign = 'center';
    ctx.fillStyle = mono ? '#1f2023' : COLOR.roomLabel;
    ctx.font = '600 13px ui-sans-serif, system-ui, sans-serif';
    ctx.fillText(room.name || roomTypeName(room.roomType), s.x, s.y - 4);
    ctx.font = '11px ui-sans-serif, system-ui, sans-serif';
    // The floor's area, inside the walls, as the app labels a room; a tap types over it.
    const area = floorArea(level, room), text = tr('#ValueSquareMeters', area.toFixed(1)), width = ctx.measureText(text).width;
    const label: DimensionLabel = { kind: 'room', index: room.index, x: s.x, y: s.y + 8, angle: 0, halfWidth: width / 2 + 6, halfHeight: 9, value: area };
    this.dimensionLabels.push(label);
    this.drawLabelPlate(label);
    ctx.fillStyle = this.labelStyle.scale ? COLOR.labelScale : mono ? '#3b3d42' : COLOR.roomLabel;
    ctx.fillText(text, s.x, s.y + 12);
    // Under a mansard, the floor ANSI Z765 counts: where the ceiling stands 5 ft (1.52 m) or higher.
    const ceiling = roomCeiling(level, room);
    if (ceiling) {
      ctx.font = '10px ui-sans-serif, system-ui, sans-serif';
      ctx.fillStyle = mono ? '#3b3d42' : COLOR.roomLabel;
      ctx.fillText(tr('#ViewPlanAreaAnsi', areaWithHeadroom(ceiling, FIVE_FEET).toFixed(1)), s.x, s.y + 25);
    }
  }

  /**
   * Under a mansard, the lines of its ceiling over the floor (`roomCeiling.ts`), as a plan draws what is over its cut: the
   * folds, hips and steps where the ceiling's surfaces meet, dashed; and where it stands 5 ft and 7 ft high, the lines
   * ANSI Z765 measures a room's finished area by — the floor outside the first does not count, and at least half of what
   * does must lie inside the second. Returns how many pieces of line were drawn.
   */
  private drawCeilingLines(level: Level): number {
    const ceilings = slopedCeilings(level);
    if (!ceilings.length) return 0;
    const ctx = this.ctx, mono = this.print === 'furniture' || this.print === 'architect';
    let drawn = 0;
    const stroke = (segments: ReadonlyArray<readonly [Vec2, Vec2]>, color: string, dash: number[], width = 1.25) => {
      if (!segments.length) return;
      ctx.beginPath();
      for (const [a, b] of segments) { const p = this.toScreen(a), q = this.toScreen(b); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y); }
      // A light edge under the dashes keeps them readable over a parquet or a tiled floor.
      if (!mono) { ctx.setLineDash([]); ctx.strokeStyle = 'rgba(255,255,255,.6)'; ctx.lineWidth = width + 2.5; ctx.stroke(); }
      ctx.strokeStyle = color; ctx.lineWidth = width; ctx.setLineDash(dash); ctx.stroke();
      drawn += segments.length;
    };
    /** A height along the longest piece of its line, upright, when the piece is long enough on screen to carry it. */
    const label = (segments: ReadonlyArray<readonly [Vec2, Vec2]>, text: string, color: string) => {
      const longest = [...segments].map(([a, b]) => [this.toScreen(a), this.toScreen(b)] as const)
        .sort(([a, b], [c, d]) => Math.hypot(d.x - c.x, d.y - c.y) - Math.hypot(b.x - a.x, b.y - a.y))[0];
      if (!longest || Math.hypot(longest[1].x - longest[0].x, longest[1].y - longest[0].y) < 72) return;
      const [a, b] = longest;
      let angle = Math.atan2(b.y - a.y, b.x - a.x);
      if (angle > Math.PI / 2) angle -= Math.PI; else if (angle < -Math.PI / 2) angle += Math.PI;
      ctx.save();
      ctx.translate((a.x + b.x) / 2, (a.y + b.y) / 2); ctx.rotate(angle);
      ctx.font = '600 10px ui-sans-serif, system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
      ctx.setLineDash([]); ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(255,255,255,.85)'; ctx.strokeText(text, 0, -3);
      ctx.fillStyle = color; ctx.fillText(text, 0, -3);
      ctx.restore();
    };
    ctx.save();
    for (const ceiling of ceilings) {
      stroke(ceilingEdges(ceiling).map(edge => [edge.from, edge.to] as const), mono ? '#4a4c52' : COLOR.ceilingEdge, [6, 4]);
      const five = headroomLine(ceiling, FIVE_FEET), seven = headroomLine(ceiling, SEVEN_FEET);
      stroke(five, mono ? '#3b3d42' : COLOR.headroomLow, [10, 3, 2, 3], 1.5);
      stroke(seven, mono ? '#6f7178' : COLOR.headroomTall, [3, 3]);
      label(five, tr('#ValueMeters', fixed(1.52, 2)), mono ? '#3b3d42' : COLOR.headroomLow);
      label(seven, tr('#ValueMeters', fixed(2.13, 2)), mono ? '#6f7178' : COLOR.headroomTall);
    }
    ctx.restore();
    return drawn;
  }

  /**
   * Behind a dimension label: nothing at rest, a light plate when it is pointed at or typed over, and a white capsule
   * with the accent's edge while the plan's scale is being set, when every label is a button (the app's capsules).
   */
  private drawLabelPlate(label: DimensionLabel): void {
    const { scale, focus } = this.labelStyle, lit = focus === dimensionLabelKey(label);
    if (!scale && !lit) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(label.x, label.y); ctx.rotate(label.angle);
    const w = label.halfWidth * 2 + (scale ? 8 : 4), h = label.halfHeight * 2 + (scale ? 4 : 2);
    ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(-w / 2, -h / 2, w, h, h / 2); else ctx.rect(-w / 2, -h / 2, w, h);
    ctx.fillStyle = scale ? (lit ? '#e8f1fc' : '#ffffff') : '#eceef2'; ctx.fill();
    if (scale) { ctx.lineWidth = lit ? 2 : 1; ctx.strokeStyle = COLOR.labelScale; ctx.stroke(); }
    ctx.restore();
  }

  /** The dimension label under a canvas point, with a finger's slack on a coarse pointer; the one drawn last wins. */
  labelAt(x: number, y: number, coarse = false): DimensionLabel | null {
    const slack = coarse ? 10 : 3;
    for (let i = this.dimensionLabels.length - 1; i >= 0; i--) {
      const label = this.dimensionLabels[i], dx = x - label.x, dy = y - label.y;
      const c = Math.cos(-label.angle), s = Math.sin(-label.angle), u = dx * c - dy * s, v = dx * s + dy * c;
      if (Math.abs(u) <= label.halfWidth + slack && Math.abs(v) <= label.halfHeight + slack) return label;
    }
    return null;
  }

  /**
   * The stairwells in the surface on the plan — in the floor, over the flights of the floor below that lead up to it, or
   * in the ceiling, over this floor's own — drawn the way an opening in a slab is drawn: the paper through it, its
   * outline and its two diagonals. Returns how many were drawn.
   */
  private drawWells(wells: readonly Stairwell[]): number {
    const ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = COLOR.well; ctx.lineWidth = 1;
    for (const well of wells) {
      const corners = well.polygon.map(point => this.toScreen(point));
      ctx.beginPath();
      corners.forEach((s, i) => { if (i) ctx.lineTo(s.x, s.y); else ctx.moveTo(s.x, s.y); });
      ctx.closePath();
      ctx.fillStyle = COLOR.paper; ctx.fill(); ctx.stroke();
      if (corners.length === 4) {
        ctx.beginPath();
        ctx.moveTo(corners[0].x, corners[0].y); ctx.lineTo(corners[2].x, corners[2].y);
        ctx.moveTo(corners[1].x, corners[1].y); ctx.lineTo(corners[3].x, corners[3].y);
        ctx.stroke();
      }
    }
    ctx.restore();
    return wells.length;
  }

  private drawRoomMoveHandle(room: Room, dragging: boolean): void {
    const ctx = this.ctx;
    ctx.save(); ctx.beginPath();
    room.polygon.forEach((p, i) => { const s = this.toScreen(p); if (i) ctx.lineTo(s.x, s.y); else ctx.moveTo(s.x, s.y); });
    ctx.closePath(); ctx.strokeStyle = COLOR.select; ctx.lineWidth = 2; ctx.stroke();
    if (!dragging) {
      const p = this.toScreen(roomMoveHandlePoint(room));
      ctx.translate(p.x, p.y); ctx.beginPath(); ctx.arc(0, 0, 17, 0, Math.PI * 2); ctx.fillStyle = '#292929'; ctx.fill();
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(-8, 0); ctx.lineTo(8, 0); ctx.moveTo(0, -8); ctx.lineTo(0, 8);
      for (const [x, y, dx, dy] of [[8, 0, -3, 3], [-8, 0, 3, 3], [0, 8, 3, -3], [0, -8, 3, 3]]) {
        ctx.moveTo(x + dx, y + dy); ctx.lineTo(x, y); ctx.lineTo(x + (x ? dx : -dx), y + (y ? dy : -dy));
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  private surfacePattern(surface: Surface | undefined): CanvasPattern | null {
    const texture = resolveTexture(surface);
    if (!texture || typeof Image === 'undefined') return null;
    let entry = this.surfaceImages.get(texture.url);
    if (!entry) {
      const image = new Image(); image.crossOrigin = 'anonymous';
      entry = { image, loaded: false }; this.surfaceImages.set(texture.url, entry);
      const pending = entry;
      image.onload = () => { pending.loaded = image.naturalWidth > 0 && image.naturalHeight > 0; this.invalidate(); };
      image.onerror = () => this.invalidate();
      image.src = texture.url;
    }
    if (!entry.loaded) return null;
    entry.pattern ??= this.ctx.createPattern(entry.image, 'repeat') ?? undefined;
    if (!entry.pattern) return null;
    const origin = this.toScreen({ x: 0, z: 0 }), scale = this.viewport.scale;
    // slabGeometry UV=(x,-z); TextureLoader flips image Y. Keep the same world origin,
    // metre repeat and offset when zooming/panning, independent of the room's bounds.
    entry.pattern.setTransform(floorPatternAffine(surface,entry.image.naturalWidth,entry.image.naturalHeight,origin,scale));
    return entry.pattern;
  }

  private drawWall(solid: WallSolid, selection: Selection, hover?: HitTarget, snapEdge = false): void {
    const ctx = this.ctx;
    const selected = selection?.kind === 'wall' && selection.id === solid.wall.id;
    const hovered = (hover?.kind === 'wall' || hover?.kind === 'wall-mid') && hover.id === solid.wall.id;
    const c = solid.corners;
    ctx.beginPath();
    for (const [i, p] of (solid.outline ?? [c.leftStart, c.leftEnd, c.rightEnd, c.rightStart]).entries()) {
      const s = this.toScreen(p);
      if (i) ctx.lineTo(s.x, s.y); else ctx.moveTo(s.x, s.y);
    }
    ctx.closePath();
    if (solid.wall.hidden) {
      // A hidden wall still bounds its room and still takes a tap: the app draws it as a dotted line
      // (Wall2DController.UpdateDottedLine), the plan as the dashed outline of its body.
      ctx.save();
      if (selected || hovered) { ctx.fillStyle = selected ? COLOR.hiddenWallSelected : COLOR.hiddenWallHover; ctx.fill(); }
      ctx.setLineDash([5, 4]); ctx.lineWidth = 1.25; ctx.strokeStyle = selected ? COLOR.select : hovered ? COLOR.wallHover : COLOR.wall;
      ctx.stroke(); ctx.restore();
      return;
    }
    // Ready is slate, a hover lightens it, a selection turns it blue and a hovered selection light blue.
    ctx.fillStyle = selected ? (hovered ? COLOR.wallSelectedHover : COLOR.select) : hovered ? COLOR.wallHover : COLOR.wall;
    ctx.fill();
    ctx.strokeStyle = selected ? COLOR.select : COLOR.wallEdge;
    ctx.lineWidth = 0.75;
    if (selected || hovered) ctx.stroke();
    else {
      // Walls meeting at a joint read as one solid: only the boundary of their union is outlined,
      // never the seam between two polygons or the part of a thin wall buried in a thick one.
      ctx.beginPath();
      for (const [a, b] of this.seams.get(this.solids)?.get(solid.wall.id) ?? []) {
        const p = this.toScreen(a), q = this.toScreen(b); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y);
      }
      ctx.stroke();
    }
    if (snapEdge) {
      // Snap active: a two-pixel turquoise line along the room side of the wall being edited.
      const [p, q] = solid.interiorIsLeft ? [c.leftStart, c.leftEnd] : [c.rightStart, c.rightEnd];
      const a = this.toScreen(p), b = this.toScreen(q);
      ctx.save(); ctx.strokeStyle = COLOR.snap; ctx.lineWidth = 2; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); ctx.restore();
    }
  }

  /**
   * An opening's symbol and where it lies: its hinge jamb on the wall's axis, the way along the opening
   * and the side a door swings to, in which `openingSymbols.ts` draws and hit-tests. The door's hand is
   * the model's: the hinge jamb and the swing side turn together when the opening is flipped.
   */
  private openingPlacement(o: Opening, wall: Wall) {
    const at = (offset: number): Vec2 => ({
      x: wall.start.x + (wall.end.x - wall.start.x) * offset / wall.length,
      z: wall.start.z + (wall.end.z - wall.start.z) * offset / wall.length,
    });
    const a = at(o.offset - o.width / 2), b = at(o.offset + o.width / 2);
    const orientation = (b.x - a.x) * wall.normalIn.z - (b.z - a.z) * wall.normalIn.x;
    const forward = (orientation > 0) !== o.flipped, side = o.flipped ? -1 : 1;
    const hinge = forward ? a : b, far = forward ? b : a, span = Math.hypot(far.x - hinge.x, far.z - hinge.z) || 1;
    return { a, b, middle: at(o.offset), hinge,
      along: { x: (far.x - hinge.x) / span, z: (far.z - hinge.z) / span },
      across: { x: wall.normalIn.x * side, z: wall.normalIn.z * side },
      frame: (wall.thickness / 2 - openingDesign(o, wall.thickness).recess) * side,
      symbol: openingSymbol(o, o.modelRef.kind === 'none' ? [] : this.furnitureLayer?.modelTags?.(o.modelRef.id) ?? []),
      // A door made here opens as far as it was set to; shut or unset, and on every catalog door, the
      // plan shows it at 90° (the Figma rules, card 13).
      angle: o.design?.style && o.design.openAngle ? o.design.openAngle : 90 };
  }

  /** The opening's symbol from the Figma board (`openingSymbols.ts`), in `state`. */
  private drawOpening(o: Opening, wall: Wall, level: Level, state: SymbolState): void {
    const ctx = this.ctx, place = this.openingPlacement(o, wall), { a, b, along, across } = place;
    const design = openingDesign(o, wall.thickness), origin = this.toScreen(place.hinge), px = 1 / this.viewport.scale;
    // The room two centimetres beyond each face; a side with none, outdoors, shows the other one's floor.
    const beyond = (sign: -1 | 1) => {
      const reach = (wall.thickness / 2 + .02) * sign;
      return roomAt(level, { x: place.middle.x + across.x * reach, z: place.middle.z + across.z * reach });
    };
    const rooms = { [-1]: beyond(-1), [1]: beyond(1) };
    const frame: SymbolFrame = {
      at: (u, v) => ({ x: origin.x + (along.x * u + across.x * v) * px, y: origin.y + (along.z * u + across.z * v) * px }),
      px, width: o.width, thickness: wall.thickness, frame: place.frame,
      ground: sign => this.fillGround(rooms[sign] ?? rooms[-sign as -1 | 1]),
    };
    drawOpeningSymbol(ctx, frame, place.symbol, state, place.angle);
    this.drawnOpeningSymbols.push({ id: o.id, state, ...place.symbol });
    if (openingKind(o) === 'window' && design.curtains !== 'none') {
      const outward = wall.thickness / 2 + .13;
      ctx.strokeStyle = '#a49a8c'; ctx.lineWidth = 3;
      for (const [start, end] of [[0, .27], [.73, 1]]) {
        const p = this.toScreen({ x: a.x + (b.x - a.x) * start + wall.normalIn.x * outward, z: a.z + (b.z - a.z) * start + wall.normalIn.z * outward });
        const q = this.toScreen({ x: a.x + (b.x - a.x) * end + wall.normalIn.x * outward, z: a.z + (b.z - a.z) * end + wall.normalIn.z * outward });
        ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y); ctx.stroke();
      }
    }
  }

  /** Fills the current path as `room`'s floor (or ceiling) is painted, and with the paper outside every room. */
  private fillGround(room: Room | undefined): void {
    const ctx = this.ctx;
    ctx.fillStyle = COLOR.paper; ctx.fill();
    if (!room) return;
    const surface = this.planSurface === 'ceiling' ? room.ceilingSurface : room.floorSurface;
    ctx.save();
    ctx.globalAlpha *= roomSurfaceOpacity(surface) ?? 1;
    ctx.fillStyle = surfaceColor(surface, this.planSurface); ctx.fill();
    // The pattern is anchored to the plan, not the path, so the floor runs on through the doorway.
    const pattern = this.surfacePattern(surface);
    if (pattern) { ctx.fillStyle = pattern; ctx.fill(); }
    ctx.restore();
  }

  private drawItemPlaceholder(item: Item): void {
    const ctx = this.ctx;
    const quad = itemFootprint(item, this.footprints);
    ctx.beginPath();
    quad.forEach((p, i) => {
      const s = this.toScreen(p);
      if (i) ctx.lineTo(s.x, s.y); else ctx.moveTo(s.x, s.y);
    });
    ctx.closePath();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#a6a4a0';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);
  }

  /** A wall's length beside it: outside the wall (`side` 1, away from its normal) or inside (-1). */
  private drawWallDimension(solid: WallSolid, side: 1 | -1 = 1): void {
    const w = solid.wall;
    const ctx = this.ctx;
    const px = w.length / this.viewport.scale;
    if (px < 40) return;
    // The chain runs a fixed distance outside the wall's face, whatever its thickness, and its
    // extension ticks start at that face rather than at the centreline — a tick from the joint's
    // centre would cut straight through the corner of a thick wall.
    const half = w.thickness / 2 * side, off = (w.thickness / 2 + 0.28) * side;
    const a = { x: w.start.x - w.normalIn.x * off, z: w.start.z - w.normalIn.z * off };
    const b = { x: w.end.x - w.normalIn.x * off, z: w.end.z - w.normalIn.z * off };
    const faceStart = { x: w.start.x - w.normalIn.x * half, z: w.start.z - w.normalIn.z * half };
    const faceEnd = { x: w.end.x - w.normalIn.x * half, z: w.end.z - w.normalIn.z * half };
    const sa = this.toScreen(a);
    const sb = this.toScreen(b);
    // Where a neighbour's body would be crossed — a thick wall meeting this one at an angle — the
    // chain and its ticks stop at that wall; a measurement never runs through a corner.
    let chains = this.chains.get(this.solids);
    if (!chains) { chains = new Map(); this.chains.set(this.solids, chains); }
    const key = side === 1 ? w.id : `${w.id}:inside`;
    let pieces = chains.get(key);
    if (!pieces) {
      const polygons = this.solids.map(other => other.outline);
      pieces = [[a, b], [faceStart, a], [faceEnd, b]].flatMap(([p, q]) => segmentOutside(p, q, polygons));
      chains.set(key, pieces);
    }
    ctx.strokeStyle = COLOR.dim;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const [p, q] of pieces) { const s1 = this.toScreen(p), s2 = this.toScreen(q); ctx.moveTo(s1.x, s1.y); ctx.lineTo(s2.x, s2.y); }
    ctx.stroke();
    const mid = { x: (sa.x + sb.x) / 2, y: (sa.y + sb.y) / 2 };
    let angle = Math.atan2(sb.y - sa.y, sb.x - sa.x);
    if (angle > Math.PI / 2 || angle < -Math.PI / 2) angle += Math.PI;
    ctx.save();
    ctx.translate(mid.x, mid.y);
    ctx.rotate(angle);
    ctx.textAlign = 'center';
    ctx.font = '600 11px ui-monospace, Menlo, monospace';
    const label = `${w.length.toFixed(2)}`;
    const tw = ctx.measureText(label).width;
    const hit: DimensionLabel = { kind: 'wall', id: w.id, x: mid.x, y: mid.y - 1.5, angle, halfWidth: tw / 2 + 4, halfHeight: 7, value: w.length };
    this.dimensionLabels.push(hit);
    const plate = this.labelStyle.scale || this.labelStyle.focus === dimensionLabelKey(hit);
    if (!plate) { ctx.fillStyle = COLOR.paper; ctx.fillRect(-tw / 2 - 3, -8, tw + 6, 13); }
    ctx.restore();
    if (plate) this.drawLabelPlate(hit);
    ctx.save();
    ctx.translate(mid.x, mid.y); ctx.rotate(angle);
    ctx.textAlign = 'center';
    ctx.font = '600 11px ui-monospace, Menlo, monospace';
    ctx.fillStyle = this.labelStyle.scale ? COLOR.labelScale : COLOR.dim;
    ctx.fillText(label, 0, 2);
    ctx.restore();
  }

  /** The selection outline: a wide translucent halo under a solid two-pixel edge. */
  private strokeFootprint(quad: readonly Vec2[], color: string): void {
    const ctx = this.ctx;
    ctx.beginPath();
    quad.forEach((point, i) => { const p = this.toScreen(point); if (i) ctx.lineTo(p.x, p.y); else ctx.moveTo(p.x, p.y); });
    ctx.closePath();
    ctx.lineWidth = 6; ctx.strokeStyle = `${color}35`; ctx.stroke();
    ctx.lineWidth = 2; ctx.strokeStyle = color; ctx.stroke();
  }

  private drawSelectionOverlay(level: Level, selection: Selection, surface: 'floor' | 'ceiling', dimensions: boolean, dragging: boolean, placing: boolean): void {
    if (selection?.kind === 'items') {
      // A gathered group wears the outline on every piece and nothing else: turning, sizes and
      // distances belong to one item.
      for (const item of level.items) if (selection.ids.includes(item.id) && isPlanItemVisible(item, surface, level.height))
        this.strokeFootprint(itemFootprint(item, this.footprints), COLOR.select);
      return;
    }
    if (selection?.kind !== 'item') return;
    const item = level.items.find((i) => i.id === selection.id);
    if (!item || !isPlanItemVisible(item, surface, level.height)) return;
    const ctx = this.ctx;
    // Everything the item being placed is currently inside of, outlined with it rather than
    // after it: the selected outline stays the topmost one. Like Unity, this marks the
    // obstruction only for the length of the gesture — a finished room is full of pieces
    // that share space on purpose, and selecting one is not a question about them.
    // A mansard's slope is in the way too, though the floor plan does not draw it: its footprint shows red.
    const conflicts = placing ? level.items.filter(other => (isPlanItemVisible(other, surface, level.height) || isAttic(other))
      && isObstruction(item, other, this.footprints)) : [];
    for (const other of conflicts) this.strokeFootprint(itemFootprint(other, this.footprints), COLOR.conflict);
    this.strokeFootprint(itemFootprint(item, this.footprints), conflicts.length ? COLOR.conflict : COLOR.select);
    const handle = this.toScreen(rotateHandlePoint(item));
    const centre = this.toScreen(item.position);
    const label = tr('#ViewPlanSizeCentimeters', (item.size.w * 100).toFixed(0), (item.size.d * 100).toFixed(0));
    ctx.font = '11px ui-monospace, Menlo, monospace';
    const tw = ctx.measureText(label).width;
    const y = centre.y + Math.max(item.size.d / 2 / this.viewport.scale, 14) + 16;
    if (!dragging) this.labelRects.push(
      { x: handle.x - 12, y: handle.y - 12, width: 24, height: 24 },
      { x: centre.x - tw / 2 - 6, y: y - 12, width: tw + 12, height: 18 },
    );
    if (dimensions && this.viewport.scale <= 1 / 12) {
      for (const marker of distanceMarkers(level, item, this.footprints, surface)) this.drawDistance(marker.from, marker.to, marker.length, COLOR.select);
    }
    if (dragging) return;
    // A locked piece turns no further: no handle to promise it (the app nulls its Drag).
    if (!item.locked) {
      // The leader stops short of the disc: a dash touching the ring reads as a defect in the icon.
      const span = Math.hypot(handle.x - centre.x, handle.y - centre.y);
      if (span > HANDLE_RADIUS + 5) {
        const t = (span - HANDLE_RADIUS - 3) / span;
        ctx.strokeStyle = COLOR.select;
        ctx.setLineDash([3, 3]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(centre.x, centre.y);
        ctx.lineTo(centre.x + (handle.x - centre.x) * t, centre.y + (handle.y - centre.y) * t);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.beginPath();
      ctx.arc(handle.x, handle.y, HANDLE_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.strokeStyle = COLOR.select;
      ctx.lineWidth = 2;
      ctx.stroke();
      drawRotateGlyph(ctx, handle.x, handle.y, COLOR.select);
    }

    // Size readout under the footprint.
    ctx.font = '11px ui-monospace, Menlo, monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(20,131,224,0.92)';
    roundRect(ctx, centre.x - tw / 2 - 6, y - 12, tw + 12, 18, 6);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.fillText(label, centre.x, y + 1);
  }

  private drawDistance(from: Vec2, to: Vec2, value: number, color: string, style: 'default' | 'primary' | 'secondary' = 'default'): void {
    const ctx = this.ctx, a = this.toScreen(from), b = this.toScreen(to);
    const distance = Math.hypot(b.x - a.x, b.y - a.y);
    if (distance < (style === 'secondary' ? .001 : 1)) return;
    const nx = -(b.y - a.y) / distance * 4, ny = (b.x - a.x) / distance * 4;
    ctx.strokeStyle = color; ctx.lineWidth = style === 'primary' ? 1.6 : 1.2; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
    for (const p of [a, b]) { ctx.moveTo(p.x - nx, p.y - ny); ctx.lineTo(p.x + nx, p.y + ny); }
    ctx.stroke();
    if (style === 'default' && distance < 26) return;
    const label = distanceLabel(value), midX = (a.x + b.x) / 2, midY = (a.y + b.y) / 2;
    ctx.font = style === 'primary' ? '700 13px system-ui' : style === 'secondary' ? '500 11px system-ui' : '600 11px system-ui'; ctx.textAlign = 'center';
    const width = ctx.measureText(label).width + 12;
    let x = midX, y = midY;
    // Keep all live measurements readable at a room-sized zoom, including equal-gap guides.
    for (const offset of [0, 24, -24, 48, -48, 72, -72, 96, -96]) {
      x = clamp(midX + nx / 4 * offset, width / 2 + 4, this.width - width / 2 - 4);
      y = clamp(midY + ny / 4 * offset, 14, this.height - 14);
      const collides = this.labelRects.some(r => x + width / 2 + 2 > r.x && x - width / 2 - 2 < r.x + r.width && y + 12 > r.y && y - 12 < r.y + r.height);
      if (!collides) break;
    }
    this.labelRects.push({ x: x - width / 2, y: y - 10, width, height: 20 });
    if (Math.hypot(x - midX, y - midY) > 1) {
      ctx.beginPath(); ctx.moveTo(midX, midY); ctx.lineTo(x, y); ctx.strokeStyle = color; ctx.lineWidth = .6; ctx.stroke();
    }
    ctx.fillStyle = style === 'primary' ? color : '#fffffff2'; roundRect(ctx, x - width / 2, y - 10, width, 20, 10); ctx.fill();
    ctx.strokeStyle = color; ctx.lineWidth = .6; ctx.stroke(); ctx.fillStyle = style === 'primary' ? '#fff' : color; ctx.fillText(label, x, y + 4);
  }

  /** The chain along an opening's wall: from each end of the wall to its jambs, the wall itself, and unless `width` is false the opening's own width. */
  private drawOpeningDimensions(opening: Opening, wall: Wall, width = true): void {
    const ctx = this.ctx, scale = this.viewport.scale;
    const at = (offset: number, outward: number): Vec2 => ({
      x: wall.start.x + (wall.end.x - wall.start.x) * offset / wall.length - wall.normalIn.x * outward,
      z: wall.start.z + (wall.end.z - wall.start.z) * offset / wall.length - wall.normalIn.z * outward,
    });
    const chainOffset = wall.thickness / 2 + 28 * scale, totalOffset = chainOffset + 34 * scale;
    const jambStart = opening.offset - opening.width / 2, jambEnd = opening.offset + opening.width / 2;
    ctx.strokeStyle = '#99958f'; ctx.lineWidth = .8; ctx.beginPath();
    for (const offset of [0, jambStart, jambEnd, wall.length]) {
      const a = this.toScreen(at(offset, wall.thickness / 2)), b = this.toScreen(at(offset, offset === 0 || offset === wall.length ? totalOffset : chainOffset));
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
    }
    ctx.stroke();
    this.drawDistance(at(0, totalOffset), at(wall.length, totalOffset), wall.length, '#292c30', 'primary');
    this.drawDistance(at(0, chainOffset), at(jambStart, chainOffset), jambStart, '#68645e', 'secondary');
    this.drawDistance(at(jambEnd, chainOffset), at(wall.length, chainOffset), wall.length - jambEnd, '#68645e', 'secondary');
    if (width) this.drawDistance(at(jambStart, chainOffset), at(jambEnd, chainOffset), opening.width, '#8a857c', 'secondary');
  }

  /**
   * A wall with doors or windows in the architect's drawing (the app's FragmentLengthBuilder and TotalLengthBuilder):
   * from the wall's end to each jamb, each opening's width and the stretches between, with the whole length beyond.
   */
  private drawWallChain(wall: Wall, openings: readonly Opening[], side: 1 | -1 = 1): void {
    const ctx = this.ctx, scale = this.viewport.scale;
    const at = (offset: number, outward: number): Vec2 => ({
      x: wall.start.x + (wall.end.x - wall.start.x) * offset / wall.length - wall.normalIn.x * outward * side,
      z: wall.start.z + (wall.end.z - wall.start.z) * offset / wall.length - wall.normalIn.z * outward * side,
    });
    const chainOffset = wall.thickness / 2 + 28 * scale, totalOffset = chainOffset + 34 * scale;
    const spans = [...openings].sort((a, b) => a.offset - b.offset)
      .map(opening => [Math.max(0, opening.offset - opening.width / 2), Math.min(wall.length, opening.offset + opening.width / 2)] as const);
    const stops = [...new Set([0, ...spans.flat(), wall.length].map(value => Math.round(value * 1e4) / 1e4))].sort((a, b) => a - b);
    ctx.strokeStyle = '#99958f'; ctx.lineWidth = .8; ctx.beginPath();
    for (const offset of stops) {
      const a = this.toScreen(at(offset, wall.thickness / 2)), b = this.toScreen(at(offset, offset === 0 || offset === wall.length ? totalOffset : chainOffset));
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
    }
    ctx.stroke();
    this.drawDistance(at(0, totalOffset), at(wall.length, totalOffset), wall.length, '#292c30', 'primary');
    for (let index = 1; index < stops.length; index++) {
      const from = stops[index - 1], to = stops[index], opening = spans.some(([lo, hi]) => Math.abs(lo - from) < 1e-3 && Math.abs(hi - to) < 1e-3);
      if (to - from > 1e-3) this.drawDistance(at(from, chainOffset), at(to, chainOffset), to - from, opening ? '#8a857c' : '#68645e', 'secondary');
    }
  }

  /** The width of an opening whose point is dragged, beside that point, on past it along the wall. */
  private drawOpeningWidthLabel(opening: Opening, wall: Wall, side: -1 | 1): void {
    const handle = this.openingResizeHandles(opening, wall).find(value => value.side === side);
    if (!handle) return;
    const ctx = this.ctx, label = distanceLabel(opening.width);
    ctx.save(); ctx.font = '700 12px system-ui'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const width = ctx.measureText(label).width + 16, height = 22;
    const dx = Math.cos(handle.angle) * side, dy = Math.sin(handle.angle) * side;
    const reach = Math.abs(dx) * width / 2 + Math.abs(dy) * height / 2 + 14;
    const x = clamp(handle.x + dx * reach, width / 2 + 4, this.width - width / 2 - 4), y = clamp(handle.y + dy * reach, height / 2 + 4, this.height - height / 2 - 4);
    ctx.fillStyle = '#292c30'; roundRect(ctx, x - width / 2, y - height / 2, width, height, height / 2); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.fillText(label, x, y + .5);
    ctx.restore();
    this.labelRects.push({ x: x - width / 2, y: y - height / 2, width, height });
  }

  /** The Wall Anchor: a turquoise dot where a wall starts or ends, a square once it holds existing geometry, red where the plan refuses it. */
  private drawAnchor(point: Vec2, snapped: boolean, valid = true): void {
    const ctx = this.ctx, q = this.toScreen(point);
    ctx.save(); ctx.fillStyle = valid ? COLOR.snap : COLOR.invalid; ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.beginPath();
    if (snapped) ctx.rect(q.x - 6, q.y - 6, 12, 12); else ctx.arc(q.x, q.y, 6, 0, Math.PI * 2);
    ctx.fill(); ctx.stroke(); ctx.restore();
  }

  /** A Matching Element: the turquoise cross on the existing geometry an alignment runs to. */
  private drawMatch(point: Vec2): void {
    const ctx = this.ctx, q = this.toScreen(point);
    ctx.save(); ctx.strokeStyle = COLOR.snapMark; ctx.lineWidth = 1.5; ctx.beginPath();
    ctx.moveTo(q.x - 4, q.y - 4); ctx.lineTo(q.x + 4, q.y + 4); ctx.moveTo(q.x + 4, q.y - 4); ctx.lineTo(q.x - 4, q.y + 4);
    ctx.stroke(); ctx.restore();
  }

  /**
   * The wall being drawn: its axis across the whole plan — turquoise on a standard angle, grey while
   * free — a translucent body at the new-wall thickness, and the anchor at the moving end.
   */
  private drawWallDraft(draft: PlanDraft, a: { x: number; y: number }, b: { x: number; y: number }): void {
    const ctx = this.ctx, span = Math.hypot(b.x - a.x, b.y - a.y);
    ctx.setLineDash([]);
    if (span > .5) {
      const ux = (b.x - a.x) / span, uy = (b.y - a.y) / span, reach = this.width + this.height;
      ctx.strokeStyle = standardAngle(draft.start, draft.end) ? COLOR.axisSnap : COLOR.axisFree; ctx.lineWidth = 1; ctx.setLineDash([6, 6]);
      ctx.beginPath(); ctx.moveTo(a.x - ux * reach, a.y - uy * reach); ctx.lineTo(a.x + ux * reach, a.y + uy * reach); ctx.stroke(); ctx.setLineDash([]);
      if (!draft.pending) {
        const half = (draft.thickness ?? DEFAULT_WALL_THICKNESS) / 2 / this.viewport.scale, nx = -uy * half, ny = ux * half;
        ctx.beginPath(); ctx.moveTo(a.x + nx, a.y + ny); ctx.lineTo(b.x + nx, b.y + ny); ctx.lineTo(b.x - nx, b.y - ny); ctx.lineTo(a.x - nx, a.y - ny); ctx.closePath();
        ctx.fillStyle = draft.valid ? COLOR.draftWall : COLOR.invalidFill; ctx.globalAlpha = .8; ctx.fill(); ctx.globalAlpha = 1;
        ctx.strokeStyle = draft.valid ? COLOR.select : COLOR.invalid; ctx.lineWidth = 1; ctx.stroke();
      }
    }
    this.drawAnchor(draft.end, !!draft.snapped, draft.valid || !!draft.pending);
  }

  private drawGeometryOverlay(level: Level, selection: Selection, draft?: PlanDraft | null, guides: PlanGuide[] = [], message?: string | null, hover?: HitTarget, overlay: PlanOverlay = {}): void {
    const ctx = this.ctx;
    const endpoints = level.walls.flatMap(w => [w.start, w.end]);
    for (const guide of guides) {
      if (guide.length !== undefined) { this.drawDistance(guide.from, guide.to, guide.length, overlay.accent ? COLOR.snap : '#ee3434'); continue; }
      if (guide.kind === 'vertex') { this.drawAnchor(guide.from, true); continue; }
      if (guide.kind === 'alignment' && overlay.accent) {
        // The Alignment Guide runs from the anchor to the geometry it lines up with — that way only —
        // and marks every endpoint on that line, so the user sees what the anchor is held against.
        const dx = guide.from.x - guide.to.x, dz = guide.from.z - guide.to.z, span = Math.hypot(dx, dz);
        const along = (p: Vec2) => (p.x - guide.to.x) * dx + (p.z - guide.to.z) * dz;
        const matches = endpoints.filter(p => span > 1e-6
          && Math.abs((p.x - guide.to.x) * dz - (p.z - guide.to.z) * dx) / span < 1e-6 && along(p) > 0)
          .filter((p, i, all) => all.findIndex(q => samePoint(p, q)) === i);
        const far = matches.reduce((best, p) => along(p) > along(best) ? p : best, guide.from);
        const a = this.toScreen(guide.to), b = this.toScreen(far);
        ctx.save(); ctx.strokeStyle = COLOR.snap; ctx.lineWidth = 1; ctx.setLineDash([5, 4]);
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); ctx.restore();
        for (const match of matches.length ? matches : [guide.from]) this.drawMatch(match);
        continue;
      }
      const a = this.toScreen(guide.from), b = this.toScreen(guide.to);
      // In a wall gesture the straight line a corner snapped onto is the two-pixel turquoise line of Snap Active.
      ctx.strokeStyle = overlay.accent ? COLOR.snap : '#ee3434'; ctx.lineWidth = overlay.accent && guide.kind === 'wall' ? 2 : 1;
      ctx.setLineDash(guide.kind === 'wall' ? [] : [5, 4]); ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); ctx.setLineDash([]);
      if (overlay.accent) continue;
      ctx.beginPath();
      for (const point of [a, b]) { ctx.moveTo(point.x - 3, point.y); ctx.lineTo(point.x + 3, point.y); ctx.moveTo(point.x, point.y - 3); ctx.lineTo(point.x, point.y + 3); }
      ctx.stroke();
    }
    ctx.setLineDash([]);
    const points: Vec2[] = [];
    for (const p of endpoints) if (!points.some(q => samePoint(p, q))) points.push(p);
    const selectedWall = selection?.kind === 'wall' ? level.walls.find(w => w.id === selection.id) : undefined;
    const vertexWall = selection?.kind === 'vertex' ? level.walls.find(w => w.id === selection.wallId) : undefined;
    const selectedVertex = selection?.kind === 'vertex' ? vertexWall?.[selection.end] : undefined;
    const hoverWall = hover?.kind === 'vertex' ? level.walls.find(w => w.id === hover.wallId) : undefined;
    const hoveredVertex = hover?.kind === 'vertex' ? hoverWall?.[hover.end] : undefined;
    const openingWall = draft?.kind === 'opening' && draft.opening ? draft.wall : undefined;
    const snapHandle = overlay.snap?.handle;
    for (const p of points) {
      const selected = selectedVertex && samePoint(p, selectedVertex);
      const endpoint = selectedWall && (samePoint(p, selectedWall.start) || samePoint(p, selectedWall.end));
      const hovered = hoveredVertex && samePoint(p, hoveredVertex);
      const openingEndpoint = openingWall && (samePoint(p, openingWall.start) || samePoint(p, openingWall.end));
      // Wall Handle states: white when ready, blue while it is the one being moved, turquoise while it holds a capture.
      const snapping = snapHandle && samePoint(p, snapHandle);
      // A picture of the plan has no handles to grab.
      if (this.print || !selected && !endpoint && !hovered && !openingEndpoint && !snapping && this.viewport.scale > .025) continue;
      const q = this.toScreen(p);
      if (hovered) { ctx.beginPath(); ctx.arc(q.x, q.y, 12, 0, Math.PI * 2); ctx.fillStyle = '#4085ff25'; ctx.fill(); }
      ctx.beginPath(); ctx.arc(q.x, q.y, snapping ? 7 : hovered ? 8 : selected ? 7 : endpoint || openingEndpoint ? 6 : 3.5, 0, Math.PI * 2);
      ctx.fillStyle = snapping ? COLOR.snap : selected || hovered ? COLOR.select : '#fff'; ctx.fill();
      ctx.strokeStyle = snapping || selected || hovered ? '#fff' : openingEndpoint ? '#292c30' : endpoint ? COLOR.select : COLOR.handleEdge;
      ctx.lineWidth = snapping || selected || endpoint || hovered || openingEndpoint ? 2 : 1; ctx.stroke();
    }
    // The corner-to-be: the wall under the pointer, or the selected one, shows a point at its middle
    // that becomes a real corner once pulled. It rests while nothing is dragged.
    if (!overlay.dragging) {
      const midWall = level.walls.find(w => PlanView.splittable(level, w)
        && (((hover?.kind === 'wall' || hover?.kind === 'wall-mid') && hover.id === w.id) || (selection?.kind === 'wall' && selection.id === w.id)));
      if (midWall) {
        const q = this.toScreen(midWall.center), grabbed = hover?.kind === 'wall-mid' && hover.id === midWall.id;
        if (grabbed) { ctx.beginPath(); ctx.arc(q.x, q.y, 12, 0, Math.PI * 2); ctx.fillStyle = '#4085ff25'; ctx.fill(); }
        ctx.beginPath(); ctx.arc(q.x, q.y, grabbed ? 7 : 5, 0, Math.PI * 2);
        ctx.fillStyle = '#fff'; ctx.fill(); ctx.strokeStyle = grabbed ? COLOR.select : COLOR.handleEdge; ctx.lineWidth = grabbed ? 2 : 1; ctx.stroke();
      }
    }
    if (overlay.invalid) {
      // Invalid geometry: the refused wall and its control point in red, where the pointer asked for them.
      ctx.save(); ctx.lineWidth = 1; ctx.strokeStyle = COLOR.invalid;
      for (const quad of overlay.invalid.quads) {
        ctx.beginPath(); quad.forEach((p, i) => { const s = this.toScreen(p); if (i) ctx.lineTo(s.x, s.y); else ctx.moveTo(s.x, s.y); }); ctx.closePath();
        ctx.fillStyle = COLOR.invalidFill; ctx.globalAlpha = .75; ctx.fill(); ctx.globalAlpha = 1; ctx.stroke();
      }
      ctx.restore();
      if (overlay.invalid.handle) this.drawAnchor(overlay.invalid.handle, false, false);
    }
    if (overlay.chain) this.drawAnchor(overlay.chain, false);
    if (draft) {
      const a = this.toScreen(draft.start), b = this.toScreen(draft.end);
      ctx.save();
      ctx.strokeStyle = draft.valid ? '#292c30' : '#df5148'; ctx.fillStyle = draft.valid ? '#292c3014' : '#df514818';
      ctx.lineWidth = 3; ctx.setLineDash([7, 5]);
      if (draft.kind === 'room') {
        ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y); ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
      } else if (draft.kind === 'opening' && draft.opening && draft.wall) {
        ctx.setLineDash([]); ctx.globalAlpha = .92;
        this.drawOpening(draft.opening, draft.wall, level, 'default');
        ctx.globalAlpha = 1;
        this.drawOpeningDimensions(draft.opening, draft.wall);
      } else if (draft.kind === 'opening') {
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
        ctx.beginPath(); ctx.arc(a.x, a.y, 14, 0, Math.PI * 2); ctx.stroke();
      } else this.drawWallDraft(draft, a, b);
      ctx.setLineDash([]); ctx.fillStyle = draft.valid ? '#292c30' : '#c72f26'; ctx.textAlign = 'center'; ctx.font = '600 13px system-ui';
      const span = Math.hypot(draft.end.x - draft.start.x, draft.end.z - draft.start.z);
      const label = draft.kind === 'room' ? tr('#ViewPlanSizeMeters', Math.abs(draft.end.x - draft.start.x).toFixed(2), Math.abs(draft.end.z - draft.start.z).toFixed(2))
        : draft.kind === 'opening' || draft.pending || span < 1e-6 ? '' : tr('#ValueMeters', span.toFixed(2));
      if (label) ctx.fillText(label, (a.x + b.x) / 2, Math.min(a.y, b.y) - (draft.kind === 'opening' ? 42 : 16));
      ctx.restore();
    }
    if (overlay.anchor) this.drawAnchor(overlay.anchor.point, overlay.anchor.snapped);
    if (message) {
      ctx.font = '12px system-ui'; ctx.textAlign = 'center';
      const width = Math.min(this.width - 24, ctx.measureText(message).width + 24);
      ctx.fillStyle = 'rgba(255,245,239,.97)'; roundRect(ctx, (this.width - width) / 2, this.height - 120, width, 32, 8); ctx.fill();
      ctx.fillStyle = '#a83724'; ctx.fillText(message, this.width / 2, this.height - 99, width - 16);
    }
  }

  selectionFrame(level: Level, selection: Selection): { x: number; y: number; width: number; height: number;
    keepOut?: Array<{ x: number; y: number; width: number; height: number }> } | null {
    let points: Vec2[] = [];
    let rotate: { x: number; y: number } | undefined;
    if (selection?.kind === 'item') {
      const item = level.items.find(i => i.id === selection.id);
      if (item) { points = itemFootprint(item, this.footprints); rotate = this.toScreen(rotateHandlePoint(item)); }
    } else if (selection?.kind === 'items') {
      points = level.items.filter(item => selection.ids.includes(item.id)).flatMap(item => itemFootprint(item, this.footprints));
    } else if (selection?.kind === 'wall' || selection?.kind === 'vertex') {
      const id = selection.kind === 'wall' ? selection.id : selection.wallId;
      const wall = level.walls.find(w => w.id === id);
      if (wall) points = selection.kind === 'wall' ? [wall.start, wall.end] : [wall[selection.end]];
    } else if (selection?.kind === 'room') points = level.rooms.find(r => r.index === selection.index)?.polygon ?? [];
    else if (selection?.kind === 'opening') {
      const o = level.openings.find(o => o.id === selection.id), w = level.walls.find(w => w.id === o?.wallId);
      if (o && w) points = [-1, 1].map(sign => ({ x: w.start.x + (w.end.x - w.start.x) * (o.offset + sign * o.width / 2) / w.length,
        z: w.start.z + (w.end.z - w.start.z) * (o.offset + sign * o.width / 2) / w.length }));
    }
    if (!points.length) return null;
    const screen = points.map(p => this.toScreen(p));
    const x = Math.min(...screen.map(p => p.x)), y = Math.min(...screen.map(p => p.y));
    const rect = this.canvas.getBoundingClientRect(), width = Math.max(...screen.map(p => p.x)) - x, height = Math.max(...screen.map(p => p.y)) - y;
    return { x: rect.left + x, y: rect.top + y, width, height,
      // Reserve the complete touch target, not just the smaller painted rotation disc.
      ...(rotate ? { keepOut: [{ x: rect.left + rotate.x - 22, y: rect.top + rotate.y - 22, width: 44, height: 44 }] } : {}),
      // A group's bar goes beside the group: over it, it would hide the next piece to be gathered.
      ...(selection?.kind === 'items' ? { keepOut: [{ x: rect.left + x, y: rect.top + y, width, height }] } : {}) };
  }

  /** An opening's two Points, at its ends on the line its leaves hinge on and its window sits on: the wall's axis unless it is recessed. */
  openingResizeHandles(opening: Opening, wall: Wall): Array<{ side: -1 | 1; x: number; y: number; angle: number }> {
    const inward = wall.thickness / 2 - openingDesign(opening, wall.thickness).recess;
    return ([-1, 1] as const).map(side => ({ side, ...this.toScreen({
      x: wall.start.x + wall.dir.x * (opening.offset + side * opening.width / 2) + wall.normalIn.x * inward,
      z: wall.start.z + wall.dir.z * (opening.offset + side * opening.width / 2) + wall.normalIn.z * inward,
    }), angle: Math.atan2(wall.dir.z, wall.dir.x) }));
  }

  /**
   * The Points in the Figma rules' colours at the editor's own 12 px size: white with a dark ring in
   * Hover, a blue ring in Selected. The one under the pointer grows; on a selected opening, and while it
   * is dragged, it also turns blue with a white ring.
   */
  /** An item's resize handle, as in 3D: a white disc with a double arrow along the axis its face moves
   * on, blue while hot (under the pointer, or its size in the resize bar) and darker while dragged. */
  private drawResizeGrip(x: number, y: number, axisAngle: number, state: 'idle' | 'hot' | 'active'): void {
    const ctx = this.ctx, radius = state === 'idle' ? 10 : 11;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.24)'; ctx.shadowBlur = 4; ctx.shadowOffsetY = 1;
    ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = state === 'idle' ? '#fff' : state === 'hot' ? COLOR.pointSelected : '#0b6cc0'; ctx.fill();
    ctx.restore();
    if (state !== 'idle') { ctx.beginPath(); ctx.arc(x, y, radius + 3, 0, Math.PI * 2); ctx.strokeStyle = 'rgba(20,131,224,0.18)'; ctx.lineWidth = 5; ctx.stroke(); }
    ctx.save(); ctx.translate(x, y); ctx.rotate(axisAngle);
    ctx.strokeStyle = state === 'idle' ? COLOR.pointSelected : '#fff'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.beginPath(); ctx.moveTo(-5.5, 0); ctx.lineTo(5.5, 0);
    for (const end of [-5.5, 5.5]) { const back = end - Math.sign(end) * 2.8; ctx.moveTo(back, -2.8); ctx.lineTo(end, 0); ctx.lineTo(back, 2.8); }
    ctx.stroke(); ctx.restore();
  }
  /** A size beside its handle: a white pill (blue while hot, faded beside another hot size), its near
   * edge 22 px out along the handle's direction, as the 3D readouts hang. */
  /** Where a size beside this handle lands: its near edge 22 px out along the handle's direction. */
  private resizeLabelRect(handle: { x: number; y: number; dir: { x: number; y: number } }, axis: ResizeFace['axis'], text: string) {
    const ctx = this.ctx, letter = tr(axis === 'w' ? '#EditorSizeLetterWidth' : axis === 'd' ? '#EditorSizeLetterDepth' : '#EditorSizeLetterHeight');
    ctx.save(); ctx.font = '600 12px system-ui, -apple-system, sans-serif';
    const letterWidth = ctx.measureText(letter).width, width = 16 + letterWidth + 4 + ctx.measureText(text).width, height = 24;
    ctx.restore();
    const across = Math.abs(handle.dir.x) >= Math.abs(handle.dir.y), ax = handle.x + handle.dir.x * 22, ay = handle.y + handle.dir.y * 22;
    return { x: across ? (handle.dir.x > 0 ? ax : ax - width) : ax - width / 2, y: across ? ay - height / 2 : (handle.dir.y > 0 ? ay : ay - height), width, height, letter, letterWidth };
  }
  private drawResizeLabel(handle: { x: number; y: number; dir: { x: number; y: number } }, axis: ResizeFace['axis'], text: string, delta: number, state: 'idle' | 'hot' | 'dim'): void {
    const ctx = this.ctx, extra = delta ? ` ${delta > 0 ? '+' : '−'}${Math.abs(delta)}` : '';
    const { x, y, width, height, letter, letterWidth } = this.resizeLabelRect(handle, axis, text + extra);
    ctx.save();
    ctx.font = '600 12px system-ui, -apple-system, sans-serif';
    ctx.globalAlpha = state === 'dim' ? .45 : 1;
    ctx.shadowColor = 'rgba(0,0,0,0.16)'; ctx.shadowBlur = 5; ctx.shadowOffsetY = 1;
    roundRect(ctx, x, y, width, height, 12); ctx.fillStyle = state === 'hot' ? COLOR.pointSelected : '#fff'; ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillStyle = state === 'hot' ? 'rgba(255,255,255,0.76)' : '#8a8a8e'; ctx.fillText(letter, x + 8, y + height / 2 + .5);
    ctx.fillStyle = state === 'hot' ? '#fff' : '#1d1d1f'; ctx.fillText(text + extra, x + 8 + letterWidth + 4, y + height / 2 + .5);
    ctx.restore();
    this.drawnResizeLabels.push({ x, y, width, height });
  }
  /** The footprint edge a hot handle moves. */
  private drawResizeEdge(item: Item, bounds: ResizeBounds, face: ResizeFace): void {
    const ctx = this.ctx, at = (x: number, z: number) => this.toScreen(localToPlan(item, x, z));
    const fixed = face.axis === 'w' ? (face.side === 1 ? bounds.max.x : bounds.min.x) : (face.side === 1 ? bounds.max.z : bounds.min.z);
    const [a, b] = face.axis === 'w' ? [at(fixed, bounds.min.z), at(fixed, bounds.max.z)] : [at(bounds.min.x, fixed), at(bounds.max.x, fixed)];
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
    ctx.strokeStyle = COLOR.pointSelected; ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.stroke(); ctx.lineCap = 'butt';
  }

  private drawOpeningResizeHandles(opening: Opening, wall: Wall, state: 'hover' | 'selected', hot?: -1 | 1, dragged = false): void {
    const ctx = this.ctx;
    for (const handle of this.openingResizeHandles(opening, wall)) {
      const under = handle.side === hot, blue = under && (dragged || state === 'selected');
      ctx.beginPath(); ctx.arc(handle.x, handle.y, under ? 7 : 6, 0, Math.PI * 2); ctx.fillStyle = blue ? COLOR.pointHot : '#fff'; ctx.fill();
      ctx.strokeStyle = blue ? '#fff' : state === 'selected' ? COLOR.pointSelected : COLOR.pointHover; ctx.lineWidth = 2; ctx.stroke();
    }
  }

  private drawScaleBar(): void {
    const ctx = this.ctx;
    const target = 120;
    const metres = niceLength(target * this.viewport.scale);
    const px = metres / this.viewport.scale;
    // Over the plane panel, level with its left edge: the chrome's gap is 32 px, 16 px on a stage up to 1100 px
    // (src/ui/app.css), and the bottom row stands 20 + 60 px high.
    const x = this.print ? 28 : this.width > 1100 ? 34 : 18;
    const y = this.height - (this.print ? 26 : 102);
    ctx.strokeStyle = COLOR.text;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, y - 4); ctx.lineTo(x, y + 4);
    ctx.moveTo(x, y); ctx.lineTo(x + px, y);
    ctx.moveTo(x + px, y - 4); ctx.lineTo(x + px, y + 4);
    ctx.stroke();
    ctx.fillStyle = COLOR.text;
    ctx.font = '11px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(metres >= 1 ? tr('#ValueMeters', metres) : tr('#ValueCentimeters', metres * 100), x + px + 8, y + 4);
  }

  // ---- hit testing ---------------------------------------------------------
  /**
   * What is under the pointer. Order matters and mirrors the app: the rotate handle wins,
   * then wall endpoints, items, openings, wall bodies and finally the room.
   */
  hit(screenX: number, screenY: number, level: Level, selection: Selection, surface: 'floor' | 'ceiling' = 'floor', resize = false, coarse = false, openingHandles = true): HitTarget {
    const p = this.toPlan(screenX, screenY);
    const grab = 10 * this.viewport.scale;

    if (selection?.kind === 'room') {
      const room = level.rooms.find(r => r.index === selection.index);
      if (room) {
        const handle = this.toScreen(roomMoveHandlePoint(room));
        if (Math.hypot(handle.x - screenX, handle.y - screenY) <= (coarse ? 22 : 18)) return { kind: 'room-move', index: room.index };
      }
    }

    // An opening's Points before everything but the room's move handle; a door's copy turned round behind
    // its twin has none of its own.
    const twins = openingTwins(level);
    if (openingHandles && selection?.kind === 'opening') {
      const shown = twins.get(selection.id) ?? selection.id;
      const opening = level.openings.find(value => value.id === shown), wall = level.walls.find(value => value.id === opening?.wallId);
      if (opening && wall) {
        const nearest = this.openingResizeHandles(opening, wall).map(handle => ({ ...handle, distance: Math.hypot(handle.x - screenX, handle.y - screenY) }))
          .filter(handle => handle.distance <= (coarse ? 22 : 14)).sort((a, b) => a.distance - b.distance)[0];
        if (nearest) return { kind: 'opening-resize', id: opening.id, side: nearest.side };
      }
    }

    if (selection?.kind === 'item') {
      const item = level.items.find((i) => i.id === selection.id);
      if (item && !item.locked && isPlanItemVisible(item, surface, level.height)) {
        if(resize){
          const face=this.resizeHandles(item).map(h=>({...h,distance:Math.hypot(h.x-screenX,h.y-screenY)}))
            .filter(h=>h.distance<(coarse ? 22 : 14)).sort((a,b)=>a.distance-b.distance)[0];
          if(face)return {kind:'resize',id:item.id,axis:face.axis,side:face.side};
        }
        const h = rotateHandlePoint(item);
        if (!resize && Math.hypot(h.x - p.x, h.z - p.z) < (coarse ? 22 : 12) * this.viewport.scale) {
          return { kind: 'rotate', id: item.id };
        }
      }
    }

    if (selection?.kind === 'wall' || selection?.kind === 'vertex') {
      const w = level.walls.find(w => w.id === (selection.kind === 'wall' ? selection.id : selection.wallId));
      if (w) for (const end of ['start', 'end'] as const) {
        if (selection.kind === 'vertex' && end !== selection.end) continue;
        if (Math.hypot(w[end].x - p.x, w[end].z - p.z) < 22 * this.viewport.scale) return { kind: 'vertex', wallId: w.id, end };
      }
    }

    if (openingHandles && !coarse) {
      let nearest: { id: string; side: -1 | 1; distance: number } | undefined;
      for (const opening of level.openings) {
        const wall = twins.has(opening.id) ? undefined : level.walls.find(value => value.id === opening.wallId);
        if (!wall) continue;
        for (const handle of this.openingResizeHandles(opening, wall)) {
          const distance = Math.hypot(handle.x - screenX, handle.y - screenY);
          if (distance <= 14 && (!nearest || distance < nearest.distance)) nearest = { id: opening.id, side: handle.side, distance };
        }
      }
      if (nearest) return { kind: 'opening-resize', id: nearest.id, side: nearest.side };
    }

    const endpoints = level.walls.flatMap(w => (['start', 'end'] as const).map(end => ({ wallId: w.id, end, distance: Math.hypot(w[end].x - p.x, w[end].z - p.z) })))
      .filter(point => point.distance < (coarse ? 22 : 14) * this.viewport.scale).sort((a, b) => a.distance - b.distance || a.wallId.localeCompare(b.wallId));
    if (endpoints[0]) return { kind: 'vertex', wallId: endpoints[0].wallId, end: endpoints[0].end };

    const middle = this.cornerToBe(level, p, coarse);
    if (middle) return { kind: 'wall-mid', id: middle.id };

    // A door or window takes a tap on its gap across the wall before the selected wall and the
    // furniture, as the app lays an opening's plan panel over everything (`OpeningFurnitureController`
    // puts it at the level's height); a leaf's swing takes it after the furniture, before the floor.
    const opening = this.openingAt(level, p, coarse);
    if (opening.gap) return { kind: 'opening', id: opening.gap };

    if (selection?.kind === 'wall') {
      const wall = level.walls.find(wall => wall.id === selection.id);
      if (wall && this.wallHit(p, wall, level, grab * .4)) {
        const toward = (p.x - wall.center.x) * wall.normalIn.x + (p.z - wall.center.z) * wall.normalIn.z;
        return { kind: 'wall', id: wall.id, side: toward >= 0 ? 'front' : 'back' };
      }
    }

    const inItems = level.items
      .filter((i) => isPlanItemVisible(i, surface, level.height) && pointInQuad(itemFootprint(i, this.footprints), p))
      .sort((a, b) => b.elevation + b.size.h - a.elevation - a.size.h || a.size.w * a.size.d - b.size.w * b.size.d);
    if (inItems.length) return { kind: 'item', id: inItems[0].id };
    if (opening.swing) return { kind: 'opening', id: opening.swing };

    for (const w of level.walls) {
      if (this.wallHit(p, w, level, grab * .4)) {
        // Which side was clicked decides which face's material the context bar edits.
        const mid = { x: (w.start.x + w.end.x) / 2, z: (w.start.z + w.end.z) / 2 };
        const toward = (p.x - mid.x) * w.normalIn.x + (p.z - mid.z) * w.normalIn.z;
        return { kind: 'wall', id: w.id, side: toward >= 0 ? 'front' : 'back' };
      }
    }

    // The tightest room under the cursor wins. A room nested inside another draws on top of it,
    // so taking whichever room came first in the list would hand back the one underneath and leave
    // the nested room impossible to pick up.
    let picked: { index: number; area: number } | null = null;
    for (const r of level.rooms) {
      if (!pointInPolygon(r.polygon, p)) continue;
      const area = Math.abs(r.polygon.reduce((sum, a, i) => {
        const b = r.polygon[(i + 1) % r.polygon.length];
        return sum + (a.x * b.z - b.x * a.z);
      }, 0) / 2);
      if (!picked || area < picked.area) picked = { index: r.index, area };
    }
    if (picked) return { kind: 'room', index: picked.index };
    return null;
  }

  /**
   * The opening whose gap across its wall is under `p`, else the first whose leaf swings over it, with
   * a finger's margin off the wall's faces. A twin standing behind the drawn copy takes neither.
   */
  private openingAt(level: Level, p: Vec2, coarse: boolean): { gap?: string; swing?: string } {
    const twins = openingTwins(level), margin = (coarse ? 16 : 8) * this.viewport.scale, found: { swing?: string } = {};
    for (const o of level.openings) {
      const wall = twins.has(o.id) ? undefined : level.walls.find(w => w.id === o.wallId);
      if (!wall) continue;
      const place = this.openingPlacement(o, wall), dx = p.x - place.hinge.x, dz = p.z - place.hinge.z;
      const at = symbolHit(place.symbol, { width: o.width, thickness: wall.thickness, frame: place.frame },
        dx * place.along.x + dz * place.along.z, dx * place.across.x + dz * place.across.z, margin, place.angle);
      if (at === 'gap') return { gap: o.id };
      if (at === 'swing') found.swing ??= o.id;
    }
    return found;
  }

  /** Whether a wall can take a corner at its middle: long enough for two walls, and no opening sitting there. */
  private static splittable(level: Level, w: Wall): boolean {
    return w.length >= MIN_WALL * 2 && !level.openings.some(o => o.wallId === w.id && Math.abs(o.offset - w.length / 2) < o.width / 2 + .02);
  }

  /** The wall whose middle is under the pointer, within the handle's grab radius. */
  private cornerToBe(level: Level, p: Vec2, coarse: boolean): Wall | undefined {
    const radius = (coarse ? 22 : 12) * this.viewport.scale;
    return level.walls.filter(w => PlanView.splittable(level, w))
      .map(w => ({ w, distance: Math.hypot(w.center.x - p.x, w.center.z - p.z) }))
      .filter(({ distance }) => distance < radius).sort((a, b) => a.distance - b.distance)[0]?.w;
  }

  resizeBounds(item:Item):ResizeBounds {
    const footprint=this.furnitureLayer?.footprint(item),bounds=fallbackResizeBounds(item);
    return footprint?{min:{x:footprint.minX,y:0,z:footprint.minZ},max:{x:footprint.maxX,y:item.size.h,z:footprint.maxZ}}:bounds;
  }
  /** The four edge handles on screen; `dir` points out of the footprint through the handle. On a small
   * footprint at a far zoom they move out along their axes until they clear each other (`anchor` keeps
   * the edge's midpoint for a leader), and the hit test and the context bar take the same places. */
  resizeHandles(item:Item):Array<ResizeFace & {x:number;y:number;angle:number;dir:{x:number;y:number};anchor?:{x:number;y:number}}>{
    const bounds=this.resizeBounds(item),result:Array<ResizeFace & {x:number;y:number;angle:number;dir:{x:number;y:number};anchor?:{x:number;y:number}}>=[];
    const centre=this.toScreen(localToPlan(item,(bounds.min.x+bounds.max.x)/2,(bounds.min.z+bounds.max.z)/2));
    // A mansard's back stays on its wall (the app drags only its depth and height): no handle there.
    const back=isAttic(item)?(Math.abs(bounds.min.z)<Math.abs(bounds.max.z)?-1:1):0;
    for(const axis of ['w','d'] as const)for(const side of [-1,1] as const){
      if(axis==='d'&&side===back)continue;
      const x=axis==='w'?(side===1?bounds.max.x:bounds.min.x):(bounds.min.x+bounds.max.x)/2;
      const z=axis==='d'?(side===1?bounds.max.z:bounds.min.z):(bounds.min.z+bounds.max.z)/2;
      const at=this.toScreen(localToPlan(item,x,z)),along=item.rotation+(axis==='w'?0:Math.PI/2),length=Math.hypot(at.x-centre.x,at.y-centre.y);
      const dir=length>1e-6?{x:(at.x-centre.x)/length,y:(at.y-centre.y)/length}:{x:Math.cos(along)*side,y:Math.sin(along)*side};
      result.push({axis,side,...at,angle:item.rotation+(axis==='w'?Math.PI/2:0),dir});
    }return separateHandles(result,PLAN_HANDLE_CLEAR,2);
  }

  private wallHit(point: Vec2, wall: Wall, level: Level, tolerance: number): boolean {
    this.solids = buildWallSolids(level);
    const outline = this.solids.find(solid => solid.wall.id === wall.id)?.outline;
    if (!outline) return distanceToSegment(point, wall.start, wall.end) < wall.thickness / 2 + tolerance;
    return pointInPolygon(outline, point) || outline.some((a, i) => distanceToSegment(point, a, outline[(i + 1) % outline.length]) < tolerance);
  }
}

// ---- geometry helpers -------------------------------------------------------
export function itemQuad(item: Item): [Vec2, Vec2, Vec2, Vec2] {
  return itemFootprint(item);
}

export function rotateHandlePoint(item: Item): Vec2 {
  return localToPlan(item, 0, item.size.d / 2 + 0.35);
}

/** Yaw that points the item's front at `to`. Inverse of `localToPlan`'s forward. */
export function yawTowards(from: Vec2, to: Vec2): number {
  return Math.atan2(to.z - from.z, to.x - from.x) - Math.PI / 2;
}

function pointInQuad(q: Vec2[], p: Vec2): boolean { return pointInPolygon(q, p); }

export function pointInPolygon(poly: Vec2[], p: Vec2): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]; const b = poly[j];
    if ((a.z > p.z) !== (b.z > p.z) && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

/** The room at a plan point: the innermost one where rooms are nested. */
function roomAt(level: Level, p: Vec2): Room | undefined {
  return level.rooms.filter(room => room.polygon.length > 2 && pointInPolygon(room.polygon, p))
    .sort((a, b) => Math.abs(polygonArea(a.polygon)) - Math.abs(polygonArea(b.polygon)))[0];
}

/** The colour a floor or ceiling is painted under its picture: its own, else the plan's default tone. */
function surfaceColor(surface: Surface | undefined, planSurface: 'floor' | 'ceiling'): string {
  return surface?.color ? `rgb(${surface.color.map(c => Math.round(clamp(c, 0, 1) * 255)).join(',')})`
    : planSurface === 'ceiling' ? '#f6f5f3' : '#d8d2c8';
}

function distanceToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x; const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  const t = len2 ? clamp(((p.x - a.x) * dx + (p.z - a.z) * dz) / len2, 0, 1) : 0;
  return Math.hypot(p.x - (a.x + dx * t), p.z - (a.z + dz * t));
}

function centroid(poly: Vec2[]): Vec2 {
  let x = 0; let z = 0;
  for (const p of poly) { x += p.x; z += p.z; }
  return { x: x / poly.length, z: z / poly.length };
}

function clamp(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }

function gridStep(scale: number): number {
  const target = 40 * scale;
  for (const s of [0.1, 0.25, 0.5, 1, 2, 5, 10]) if (s >= target) return s;
  return 20;
}

function niceLength(v: number): number {
  for (const s of [0.1, 0.25, 0.5, 1, 2, 5, 10, 20]) if (s >= v) return s;
  return 50;
}

function clip(s: string, n: number): string { return s.length > n ? `${s.slice(0, n - 1)}…` : s; }

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** The label of an unnamed room: its type's title from the app's list (`src/state/roomTypes.ts`), else «Помещение». */
export function roomTypeName(type: string): string {
  return type.trim() ? roomTypeTitle(type) ?? tr('#ViewPlanUnnamedRoom') : tr('#ViewPlanUnnamedRoom');
}

export function roomMoveHandlePoint(room: Room): Vec2 {
  const polygon = room.polygon;
  let best = roomProbe(polygon), clearance = -1;
  const xs = polygon.map(p => p.x), zs = polygon.map(p => p.z);
  const x0 = Math.min(...xs), x1 = Math.max(...xs), z0 = Math.min(...zs), z1 = Math.max(...zs);
  for (let x = 1; x < 10; x++) for (let z = 1; z < 10; z++) {
    const point = { x: x0 + (x1 - x0) * x / 10, z: z0 + (z1 - z0) * z / 10 };
    if (!contains(polygon, point)) continue;
    const distance = Math.min(...polygon.map((a, i) => projectPoint(point, a, polygon[(i + 1) % polygon.length]).distance));
    if (distance > clearance) { best = point; clearance = distance; }
  }
  return best;
}

/** The app's «Экспортировать план» drawings (`FloorPlanTypesList`): what you see, and two in black and white. */
export type PlanExportStyle = 'colour' | 'furniture' | 'architect';

/**
 * The floor as an image, fitted with a margin for its dimensions: `colour` is the plan as it is drawn (the app's
 * «Полноцветный скриншот», MakeScreenshot2D), `furniture` the walls in black on white with the rooms and the furniture in
 * grey (its «План этажа с мебелью», MakeRealEstate2D), `architect` the same with every wall's length (its «Архитектурный
 * план этажа», MakeDesignProject2D). `longSide` is the image's longer side in CSS pixels, drawn at `dpr`. The floors'
 * pictures are waited for, up to `wait` ms. Null for a floor with nothing on it.
 */
export async function renderPlanImage(level: Level, style: PlanExportStyle, options: { furniture?: PlanFurnitureLayer | null; wells?: readonly Stairwell[];
  dimensions?: boolean; longSide?: number; dpr?: number; wait?: number } = {}): Promise<HTMLCanvasElement | null> {
  const points = [...level.walls.flatMap(wall => [wall.start, wall.end]), ...level.rooms.flatMap(room => room.polygon)];
  if (!points.length) return null;
  const xs = points.map(p => p.x), zs = points.map(p => p.z);
  const spanX = Math.max(Math.max(...xs) - Math.min(...xs), 1), spanZ = Math.max(Math.max(...zs) - Math.min(...zs), 1);
  // Room round the walls for their dimension rows (about 70 px) and under them for the scale bar.
  const pad = 80, longSide = options.longSide ?? 1400;
  const scale = Math.max(spanX, spanZ) / Math.max(200, longSide - 2 * pad);
  const width = Math.round(spanX / scale + 2 * pad), height = Math.round(spanZ / scale + 2 * pad + 24);
  const canvas = document.createElement('canvas');
  let redraw = () => {};
  const view = new PlanView(canvas, () => redraw());
  try {
    view.setExportSize(width, height, options.dpr ?? 2);
    view.setFurnitureLayer(options.furniture ?? null);
    view.print = style;
    view.viewport.scale = scale;
    view.viewport.centre = { x: (Math.max(...xs) + Math.min(...xs)) / 2, z: (Math.max(...zs) + Math.min(...zs)) / 2 + 12 * scale };
    const mono = style !== 'colour';
    const draw = () => {
      // Walls in black for the two drawings; the screen's palette comes back once the image is drawn.
      const saved = { wall: COLOR.wall, wallEdge: COLOR.wallEdge };
      if (mono) Object.assign(COLOR, { wall: '#1d1e21', wallEdge: '#000000' });
      // The estate agent's drawing has no dimensions, the architect's every one; the colour plan follows the screen.
      try { view.draw(level, null, { dimensions: style === 'architect' || style === 'colour' && !!options.dimensions, surface: 'floor', wells: options.wells, openingHandles: false }); }
      finally { Object.assign(COLOR, saved); }
    };
    draw();
    if (!mono) {
      const until = performance.now() + (options.wait ?? 4000);
      await new Promise<void>(done => {
        const check = () => view.surfacesSettled() || performance.now() > until ? done() : setTimeout(check, 50);
        redraw = () => {}; check();
      });
      draw();
    }
    return canvas;
  } finally {
    redraw = () => {};
    view.dispose();
  }
}
